/**
 * VaultPage — "The Vault": CorpBrain's enterprise file system.
 *
 * Drive-style two-panel workspace:
 *   left  — "+ New" creation menu + folder navigator (lazy tree, tenant-labeled roots)
 *   right — folder workspace: breadcrumb, access chip, icon toolbar, AI rollup,
 *           drag-anywhere upload, and a rich file table (type icons, size, status).
 *
 * Action model (Drive semantics): everything that CREATES lives under the single
 * "+ New" button; everything that acts on the current folder is an icon button
 * with a tooltip on the title row; Share keeps its label as the headline
 * collaborative action. Uploads are drag-anywhere over the workspace.
 *
 * Open to EVERY signed-in user: managers see their tenants' trees, everyone
 * else sees exactly the folders shared with them (additive vault_shares
 * union) — with per-node rights driving which controls even render.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { endPlacement, mirrorPlacement } from '@/utils/rtl';
import {
  Button, Dropdown, Tree, Table, Input, Modal, Progress, Spin, Switch, Tooltip, message,
  notification,
} from 'antd';
import type { MenuProps } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useLanguage } from '@/hooks/useLanguage';
import { activeLocale } from '@/utils/locale';
import { useAuth } from '@modules/auth';
import { Sidebar } from '@/components/layout/Sidebar';
import { vaultService } from '../vaultService';
import type { VaultFolder, VaultFile, VaultAccess, RenameImpact, FolderDeleteImpact, FileDeleteImpact, VaultOrgSettings, MoveImpact, SharedFileEntry, VaultLimits, BulkAction, BulkBlockReason, BulkCheckItem, BulkDeleteImpact, BulkItem, BulkOutcome, BulkVerb } from '../types';
import { DeleteTruthRows, ImpactRow, subjectLabel } from '../components/VaultImpact';
import { blobErrorMessage, triggerBlobDownload } from '@/utils/download';
import { VaultShareDrawer } from '../components/VaultShareDrawer';
import { VaultFileViewer } from '../components/VaultFileViewer';
import { VaultUploadTray, type UploadItem } from '../components/VaultUploadTray';
import { VaultDriveCopyBadge } from '../components/VaultDriveCopyBadge';
import { useFeature } from '@hooks/useFeature';
// The composer's own Drive pieces, reused whole: the same "is Drive connected"
// answer, the same picker (recent / search / browse / pasted link), the same
// glyph. Deep imports, as with data-bundles below, to keep the chat surface
// out of this chunk.
import { useDriveConnected } from '@modules/conversations/hooks/useDriveConnected';
import { DriveFilePicker } from '@modules/conversations/components/drive/DriveFilePicker';
import { GoogleDriveGlyph } from '@modules/conversations/components/drive/driveIcons';
import type { DriveFileRef } from '@/types/models';
import VaultSummarySheet from '../components/VaultSummarySheet';
import VaultAccessMapDrawer from '../components/VaultAccessMapDrawer';
import { VaultMoveDialog, type MoveSource } from '../components/VaultMoveDialog';
// Deep import (not the module index) — the data-bundles index re-exports pages
// that would drag the whole bundle surface into this chunk.
import { UrlResearchSection } from '@modules/data-bundles/components/UrlResearchSection';
import { emitJourneyEvent, JOURNEY_EVENTS } from '@modules/journey/detect';
import './VaultPage.css';

/** The one tree row that is not a folder: the one being named. */
const DRAFT_KEY = '__vault-draft-folder__';
/* How many Drive files one pick may bring in — the server's per-request cap
   (MAX_DRIVE_FILES_PER_BATCH); each is still sent as its own upload. */
const DRIVE_PICKS_PER_BATCH = 20;
/* Where the navigator remembers whether the organization folders are
   hidden. A view preference, so it lives in the browser rather than
   costing a column and a round trip. */
/* One object, reused. A new one per render made antd remount the input, which
   blurred it, which committed the name, which re-rendered — the update loop. */
const DRAFT_ROW = { key: DRAFT_KEY, title: '', folder: {}, isLeaf: true } as unknown as TreeNodeData;

/* The three-section navigator (Drive's mental model): My files, the
   Organization's shared ground, and what other people handed you. Headers are
   rows in the same tree — non-selectable, undraggable, just labels. */
const SECTION_PREFIX = '__vault-section-';
const sectionKey = (s: string) => `${SECTION_PREFIX}${s}__`;

/** Our own drag payload type — never confused with the OS 'Files' drag that
 *  the upload overlay listens for. The payload IS a move source: what is
 *  dragged and what a "Move to…" menu names are the same thing. */
const VAULT_DND = 'application/x-corpbrain-vault-node';

type DragPayload = MoveSource;

/* ── icons (stroke inherits currentColor; RTL-neutral shapes) ── */
const FolderIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);

/* The organization's folder wears a building, not a folder: opening it means
   standing in the one place the whole organization (and the group admins
   above it) can see and its managers control. */
const OrgFolderIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
    <path d="M3 21h18" />
    <path d="M5 21V7l7-4 7 4v14" />
    <path d="M9 9h1.5M13.5 9H15M9 12.5h1.5M13.5 12.5H15" />
    <path d="M10 21v-4h4v4" />
  </svg>
);

const KebabIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="5" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="12" cy="19" r="1.7" />
  </svg>
);

const FolderPlusIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M12 11v4M10 13h4" />
  </svg>
);

const VaultMark = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M12 11v4M10 13h4" strokeLinecap="round" />
  </svg>
);

const PeopleMapIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="6" cy="6" r="2.5" />
    <circle cx="18" cy="6" r="2.5" />
    <circle cx="12" cy="18" r="2.5" />
    <path d="M7.6 8.2 11 15.5M16.4 8.2 13 15.5" />
  </svg>
);

const PlusIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const CaretIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const PencilIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.83 2.83 0 0 1 4 4L7.5 20.5 3 21l.5-4.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
  </svg>
);

/** Move: an arrow leaving for somewhere else. RTL-neutral enough — the
 *  gesture it names (drag to another folder) has no reading direction. */
const MoveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19v-8a3 3 0 0 1 3-3h12" />
    <path d="M15 4l4 4-4 4" />
  </svg>
);

const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const SharePersonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19c.7-3 3-4.5 5.5-4.5s4.8 1.5 5.5 4.5" />
    <path d="M18 8v6M15 11h6" />
  </svg>
);

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

/* The same eye, struck through: the organization folders are hidden. Only
   what you SEE changes - nothing is created, moved or deleted by the toggle. */
const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.6 6.2A9.9 9.9 0 0 1 12 6c6.5 0 10 6 10 6a17 17 0 0 1-2.4 3.2M6.5 6.6A16.6 16.6 0 0 0 2 12s3.5 6 10 6a9.7 9.7 0 0 0 4.4-1" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <path d="M3 3l18 18" />
  </svg>
);

const DownloadIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 4v12M6 12l6 6 6-6M4 20h16" />
  </svg>
);

const UploadIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 16V4M6 10l6-6 6 6M4 20h16" />
  </svg>
);

const FilePlusIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5M12 12v5M9.5 14.5h5" />
  </svg>
);

/**
 * Nobody else reaches this — one person, not a padlock.
 *
 * A lock reads as "you cannot open this", which is the opposite of what it
 * means here: the folder is yours alone. A single figure says "only you"
 * without ever suggesting a refusal.
 */
const OnlyYouIcon = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="3.4" />
    <path d="M5 20c.9-3.6 3.7-5.4 7-5.4s6.1 1.8 7 5.4" />
  </svg>
);

/** A folder someone handed you. The tooltip says who. */
const SharedInIcon = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const EmptyFolderArt = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);

function fileKind(name?: string, type?: string): 'pdf' | 'doc' | 'deck' | 'sheet' | 'text' {
  const ext = (name || '').split('.').pop()?.toLowerCase() || '';
  if (ext === 'pdf' || type === 'application/pdf') return 'pdf';
  // A deck is its own kind of thing, not a document. It used to share the Word
  // icon, so a folder of decks and memos read as one undifferentiated blue
  // list — and a deck is the one file here you open expecting slides.
  if (['ppt', 'pptx', 'pps', 'ppsx', 'odp', 'key'].includes(ext)
      || (type || '').includes('presentation')) return 'deck';
  if (['doc', 'docx', 'odt', 'pages'].includes(ext)) return 'doc';
  if (['xls', 'xlsx', 'xlsm', 'csv', 'tsv', 'numbers'].includes(ext)) return 'sheet';
  return 'text';
}

const FILE_GLYPH: Record<string, JSX.Element> = {
  pdf: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </svg>
  ),
  doc: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h6" />
    </svg>
  ),
  // A slide on its stand — the shape does the telling, so a deck is
  // recognisable in a long list before its colour registers.
  deck: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M7 8.5h7M7 11.5h4" />
      <path d="M12 16v3M9 21l3-2 3 2" />
    </svg>
  ),
  sheet: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M4 10h16M10 4v16" />
    </svg>
  ),
  text: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </svg>
  ),
};

/* Navigator width: dragged like an editor's sidebar, remembered per browser.
   The bounds keep it usable — narrow enough to give the workspace room, wide
   enough to read a long folder name and its organization together. */
/* How long to keep watching for a summary that is still being written. Six
   seconds is the usual wait; the ceiling covers a folder catching up on a
   backlog of files that were never described. */
const SUMMARY_POLL_MS = 5000;
const SUMMARY_POLL_LIMIT = 60;

const NAV_WIDTH_KEY = 'vault.navWidth';
const NAV_DEFAULT = 300;
const NAV_MIN = 200;
const NAV_MAX = 620;

/* ── Which file-table columns fit, decided by the PANEL's width ──────────
   The navigator is resizable, so the viewport says little about the room the
   table actually has. Names matter most: the name column has a floor, and
   the columns beside it give way in a fixed order — the summary first (it
   only shows while it can have SUMMARY_MIN beside a full-width name), then
   the file size, then the owner. The checkbox, the date and the row menu
   always stay. Widths are what the table is laid out with (fixed layout),
   10px cell padding included. */
const COL_W = { select: 40, owner: 110, modified: 150, size: 120, actions: 48 } as const;
const NAME_MIN = 220;
const SUMMARY_MIN = 150;
/** The name's share of the room the fixed columns leave, while a summary is shown. */
const NAME_SHARE = 0.6;

type DroppableColumn = 'summary' | 'size' | 'owner';

function planFileColumns(tableWidth: number): {
  hidden: Set<DroppableColumn>;
  /** The name's width while a summary is beside it; undefined = the rest. */
  nameWidth?: number;
} {
  const hidden = new Set<DroppableColumn>();
  // Not measured yet (first paint): everything, the name taking its share.
  if (!tableWidth) return { hidden };
  const flex = () => tableWidth - COL_W.select - COL_W.actions - COL_W.modified
    - (hidden.has('owner') ? 0 : COL_W.owner) - (hidden.has('size') ? 0 : COL_W.size);
  if (flex() < NAME_MIN + SUMMARY_MIN) hidden.add('summary');
  for (const next of ['size', 'owner'] as const) {
    if (flex() >= NAME_MIN) break;
    hidden.add(next);
  }
  return {
    hidden,
    nameWidth: hidden.has('summary')
      ? undefined
      : Math.max(NAME_MIN, Math.round(flex() * NAME_SHARE)),
  };
}

interface TreeNodeData extends DataNode {
  folder: VaultFolder;
  /** Roots carry their organization underneath; children do not. */
  tenantLabel?: boolean;
  /** Set on section-header rows ('mine' | 'org' | 'shared') — labels, not folders. */
  section?: string;
  /** Set on "Shared with me" FILE rows — a single file someone handed you.
      Clicking opens the viewer; it has no folder to select. */
  sharedFile?: SharedFileEntry;
}

export function VaultPage() {
  const { t, language, direction } = useLanguage();
  const { hasRole, user } = useAuth();

  // Journey detection: seeing the Library IS the Library step's Try — the
  // journey shows what lives here and never forces an upload.
  useEffect(() => {
    emitJourneyEvent(JOURNEY_EVENTS.libraryVisited);
  }, []);

  /* Translations are read through a ref inside the loaders below.
     A loader must not be re-created because the translation function changed
     identity — that is what turns a click into a refetch. It only ever needs
     `t` to phrase an error at the moment one happens, which is exactly what a
     ref gives it. */
  const tRef = useRef(t);
  tRef.current = t;

  const [treeData, setTreeData] = useState<TreeNodeData[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [rootsLoading, setRootsLoading] = useState(true);
  /* Find something by typing its name — in the folder you are in, and across
     the folders you can see. A file system without this is a filing cabinet. */
  const [fileQuery, setFileQuery] = useState('');
  const [treeQuery, setTreeQuery] = useState('');
  /* Is the ORGANIZATION section rolled up? The system makes one organization
     folder per organization, so a group admin over several opens the navigator
     to a stack of scaffolding above the folders they actually work in. Rolling
     it up leaves the heading and takes the rows away.

     HIDDEN on every visit (operator decision 2026-09-07 — the org shelves
     crowd out the folders people actually work in); the eye opens them for
     the session. */
  const [orgCollapsed, setOrgCollapsed] = useState(true);

  /* Typing in "Find a folder" asks the SERVER, not the loaded tree.
     The tree is lazy — roots first, children only when a branch is opened — so
     filtering it in memory could only ever find what you had already expanded.
     A folder three levels down that you had never opened was unfindable, which
     is most of a file system. The endpoint already existed and answers with the
     nested matches; this is the wiring. */
  const [searchHits, setSearchHits] = useState<{
    folders: { folder_id: string; folder_name: string; tenant_name?: string | null }[];
    files: { file_id: string; folder_id: string; file_display_name: string; folder_name?: string | null }[];
    capped?: boolean;
  } | null>(null);
  const [searching, setSearching] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<VaultFolder | null>(null);
  const [access, setAccess] = useState<VaultAccess | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rollupOpen, setRollupOpen] = useState(false);
  const [accessMapOpen, setAccessMapOpen] = useState(false);
  // Where a right-click landed on the navigator's DEAD SPACE (anywhere that
  // is not a folder row or a control): the root-folder menu opens there.
  // Deep link: /library?folder=<id> opens straight on that folder —
  // the clickable paths on the agent/skill/organization editors land
  // here. Permission is the server's call: selectFolder simply asks.
  const deepLinkedFolder = useRef<string | null>(
    new URLSearchParams(window.location.search).get('folder'));
  const [blankMenuAt, setBlankMenuAt] = useState<{ x: number; y: number } | null>(null);

  // The dead-space menu must die exactly like the folder rows' context
  // menus: any click or right-click outside it, Escape, or scrolling —
  // being controlled (cursor-anchored), it gets none of that for free.
  useEffect(() => {
    if (!blankMenuAt) return undefined;
    const away = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest('.vault-blank-ctx')) return;
      setBlankMenuAt(null);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setBlankMenuAt(null); };
    document.addEventListener('mousedown', away, true);
    document.addEventListener('contextmenu', away, true);
    document.addEventListener('scroll', away, true);
    document.addEventListener('keydown', esc, true);
    return () => {
      document.removeEventListener('mousedown', away, true);
      document.removeEventListener('contextmenu', away, true);
      document.removeEventListener('scroll', away, true);
      document.removeEventListener('keydown', esc, true);
    };
  }, [blankMenuAt]);
  /* A folder being named. It exists in the tree before it exists on the server:
     the row appears where it will live, with its name selected, and typing
     replaces it — the way a desktop does it. `parentId` is null for a top-level
     folder. `busy` keeps a slow create from being submitted twice. */
  const [draftFolder, setDraftFolder] = useState<
    { parentId: string | null; name: string; busy: boolean } | null
  >(null);
  const draftDone = useRef(false);   // set when Enter/Escape already settled it
  const committing = useRef(false);  // one create per draft, however blur fires
  /* Rights for a folder someone right-clicked, which is usually NOT the folder
     they have open. Cached per folder so opening the same menu twice is free. */
  const [menuAccess, setMenuAccess] = useState<Record<string, VaultAccess>>({});
  /* Which folder an upload is destined for. The picker can be opened from a
     right-click on a folder that is not the one on screen, so the target has to
     be remembered rather than read off `detail` when the files come back. */
  const uploadTarget = useRef<string | null>(null);
  /* "From Google Drive" exists only while the tenant has the connector on AND
     this person has actually linked an account. Otherwise the Upload entry is
     the single item it always was — no disabled row, no hint — exactly the
     composer's rule. */
  const driveAvailable = useFeature('integrations.cloud_storage');
  const driveConnected = useDriveConnected(driveAvailable);
  const [drivePickerOpen, setDrivePickerOpen] = useState(false);
  /* A folder awaiting "yes, delete it". Held as the folder itself, because the
     menu can be opened on one that is not currently selected. */
  const [confirmDelete, setConfirmDelete] = useState<VaultFolder | null>(null);
  /* What that delete would REALLY destroy — fetched when the dialog opens, so
     the confirmation states facts, not a generic "are you sure". null while
     loading; the dialog still works if the preview endpoint fails. */
  const [deleteImpact, setDeleteImpact] = useState<FolderDeleteImpact | null>(null);
  /* A file placement awaiting "yes, remove it" — with the same truth: does
     the file die outright, or does it live on somewhere else? */
  const [confirmRemove, setConfirmRemove] = useState<VaultFile | null>(null);
  const [removeImpact, setRemoveImpact] = useState<FileDeleteImpact | null>(null);
  /* "Move to…" / "Copy to…" dialog. presetDest is set when a drag-and-drop
     already chose the destination and only the consequences need confirming. */
  const [moveState, setMoveState] = useState<{
    source: MoveSource;
    mode?: 'move' | 'copy' | 'place';
    presetDest?: { folderId: string; name: string } | null;
    presetImpact?: MoveImpact | null;
  } | null>(null);
  /* The Organization folder's visible rules (charter banner + admin switch). */
  const [orgSettings, setOrgSettings] = useState<VaultOrgSettings | null>(null);
  /* Renaming happens in place, the way it does on a desktop: click the name of
     something already selected (or press F2), type, press Enter. */
  const [renaming, setRenaming] = useState<
    { kind: 'folder' | 'file'; id: string; original: string; where: 'tree' | 'title' } | null
  >(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);

  /* ── the navigator's width, dragged like an editor's sidebar ──
     Remembered across visits: a person who widened it to read long folder
     names should not have to do it again on every page load. */
  const [navWidth, setNavWidth] = useState(() => {
    const saved = Number(localStorage.getItem(NAV_WIDTH_KEY));
    return saved >= NAV_MIN && saved <= NAV_MAX ? saved : NAV_DEFAULT;
  });
  const navDrag = useRef<{ startX: number; startWidth: number } | null>(null);
  // The width the pointer is AT, not the width React has rendered. pointerup
  // can arrive before the effect re-registers with the last move's state, and
  // the save then trailed the real width by one step — a reload snapped the
  // panel back by exactly one drag increment.
  const navWidthLive = useRef(navWidth);

  const startNavDrag = (e: React.PointerEvent) => {
    e.preventDefault();
    // Capture the pointer so the release comes back here even if it happens
    // outside the window. Without it, letting go off-screen strands the drag
    // and the panel then resizes on every idle mouse move.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    navDrag.current = { startX: e.clientX, startWidth: navWidth };
  };

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!navDrag.current) return;
      // Belt and braces: if the button is no longer held, the release was lost.
      if (e.buttons === 0) {
        navDrag.current = null;
        localStorage.setItem(NAV_WIDTH_KEY, String(navWidthLive.current));
        return;
      }
      // In RTL the panel is on the right, so dragging right must make it
      // narrower. Reading the document direction keeps one handler honest for
      // both, instead of a second one that drifts.
      const rtl = document.documentElement.dir === 'rtl';
      const delta = (e.clientX - navDrag.current.startX) * (rtl ? -1 : 1);
      const next = Math.min(NAV_MAX, Math.max(NAV_MIN, navDrag.current.startWidth + delta));
      navWidthLive.current = next;
      setNavWidth(next);
    };
    const onUp = () => {
      if (!navDrag.current) return;
      navDrag.current = null;
      localStorage.setItem(NAV_WIDTH_KEY, String(navWidthLive.current));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [navWidth]);

  // The access map is an organization-wide view of both permission systems, so
  // it is gated on the role, not on whichever folder happens to be selected.
  const isManager = hasRole('admin') || hasRole('group_admin') || hasRole('super_admin');

  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | undefined>();
  /* The batch, as a list a person can read. One anonymous bar could not say
     WHICH file was going up, how many were left, or that three had been
     refused — and the refusals arrived as toasts that faded while the bar was
     still moving. */
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
  /* A ref, not the state flag: two drops in the same tick would both read the
     same stale `uploading` and both start. */
  const uploadingRef = useRef(false);

  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /* A separate input, because `webkitdirectory` is a property of the INPUT and
     an input that has it can no longer pick loose files. Two pickers is the
     only way to offer both. */
  const folderInputRef = useRef<HTMLInputElement | null>(null);

  const [shareNode, setShareNode] = useState<
    { folder_id?: string; file_id?: string; name: string } | null
  >(null);
  const [viewFile, setViewFile] = useState<VaultFile | null>(null);
  /* A click on a row opens the file. It opens on the View tab, and whether the
     Edit tab is there at all follows the rights the person already has — so
     nobody has to find the right little button first. The name is the one part
     of the row that does something else: for someone who may rename, clicking
     it renames. */
  const openFile = (row: VaultFile, inEditMode = false) => {
    setViewFile(inEditMode ? ({ ...row, openInEditMode: true } as VaultFile) : row);
  };

  /* A tree node is data only. What it LOOKS like is decided at render time by
     renderTreeTitle, so a node can turn into an editor the moment someone
     clicks its name — a title baked in here would never update. */
  const toNode = useCallback((f: VaultFolder, tenantLabel = false): TreeNodeData => ({
    key: f.folder_id,
    title: f.folder_name,
    folder: f,
    tenantLabel,
    isLeaf: false,
  }), [])

  /* ── roots ── */
  /* True once a tree has been drawn. A later refresh — after creating or
     deleting a folder — then updates the navigator in place instead of blanking
     it to a spinner, which reads as the page reloading. */
  const treeDrawn = useRef(false);

  const loadRoots = useCallback(async () => {
    if (!treeDrawn.current) setRootsLoading(true);
    try {
      // Folder roots and single-file shares arrive together: the "Shared
      // with me" section lists both, the way Drive's does.
      const [roots, sharedFiles] = await Promise.all([
        vaultService.listRootFolders(),
        vaultService.listSharedFiles().catch(() => [] as SharedFileEntry[]),
      ]);
      const tenants = new Set(roots.map((r) => r.tenant_id));
      const showTenant = tenants.size > 1;
      // The three-section navigator, Drive's mental model: your own ground,
      // the organization's shared ground, and what people handed you. A
      // header is drawn only above a group that has anything in it — and only
      // when there is more than one group, so a person with nothing but their
      // own folders is not told "My files" about everything they can see.
      const mine = roots.filter((r) => !r.org_root && !r.shared_with_me);
      const org = roots.filter((r) => r.org_root);
      const shared = roots.filter((r) => !r.org_root && r.shared_with_me);
      const fileLeaf = (f: SharedFileEntry): TreeNodeData => ({
        key: `sharedfile:${f.file_id}`,
        title: f.file_name,
        sharedFile: f,
        folder: {} as VaultFolder,
        isLeaf: true,
      } as TreeNodeData);
      setTreeData((prev) => {
        // Carry over branches that are already open. Rebuilding the roots from
        // scratch would throw away every loaded child, collapsing whatever the
        // person had expanded and making the tree re-fetch it.
        const openBranches = new Map(prev.map((n) => [n.key, n.children]));
        const withKids = (r: VaultFolder) => {
          const node = toNode(r, showTenant);
          const kids = openBranches.get(r.folder_id);
          return kids ? { ...node, children: kids } : node;
        };
        const groups = [
          { section: 'mine', nodes: mine.map(withKids) },
          { section: 'org', nodes: org.map(withKids) },
          {
            section: 'shared',
            nodes: [...shared.map(withKids), ...sharedFiles.map(fileLeaf)],
          },
        ].filter((s) => s.nodes.length);
        if (groups.length <= 1) return groups.flatMap((s) => s.nodes);
        return groups.flatMap((s) => [
          {
            key: sectionKey(s.section),
            title: '',
            section: s.section,
            folder: {} as VaultFolder,
            isLeaf: true,
            selectable: false,
            checkable: false,
            className: 'vault-section-row',
          } as TreeNodeData,
          ...s.nodes,
        ]);
      });
      treeDrawn.current = true;
    } catch (e: any) {
      message.error(e?.response?.data?.error || tRef.current('vault.loadFailed'));
    } finally {
      setRootsLoading(false);
    }
  }, [toNode]);

  useEffect(() => {
    loadRoots();
  }, [loadRoots]);

  /** Take a folder out of the tree — it is not there any more. */
  const dropNode = useCallback(
    (nodes: TreeNodeData[], key: string): TreeNodeData[] =>
      nodes
        .filter((n) => n.key !== key)
        .map((n) => (n.children
          ? { ...n, children: dropNode(n.children as TreeNodeData[], key) } : n)),
    [],
  );

  const patchChildren = useCallback(
    (nodes: TreeNodeData[], key: string, children: TreeNodeData[]): TreeNodeData[] =>
      nodes.map((n) => {
        if (n.key === key) return { ...n, children };
        if (n.children) return { ...n, children: patchChildren(n.children as TreeNodeData[], key, children) };
        return n;
      }),
    [],
  );

  const onLoadData = useCallback(
    async (node: TreeNodeData) => {
      const folder = await vaultService.getFolder(node.folder.folder_id);
      // The tree is a map of WHERE things live. A folder shared onto the
      // organization's shelf is shown inside that folder's contents — but it
      // physically lives in its owner's Library, and putting it in the tree
      // here would draw the same folder in two places at once (antd then
      // warns about the repeated key, and selecting one highlights both).
      // The contents list is where you click into it.
      const kids = (folder.children || [])
        .filter((c) => !c.shared_onto_shelf)
        .map((c) => toNode(c));
      setTreeData((prev) => patchChildren(prev, node.folder.folder_id, kids));
    },
    [patchChildren, toNode],
  );

  /* breadcrumb path from the loaded tree */
  const findPath = useCallback(
    (nodes: TreeNodeData[], id: string, trail: VaultFolder[] = []): VaultFolder[] | null => {
      for (const n of nodes) {
        const next = [...trail, n.folder];
        if (n.folder.folder_id === id) return next;
        if (n.children) {
          const hit = findPath(n.children as TreeNodeData[], id, next);
          if (hit) return hit;
        }
      }
      return null;
    },
    [],
  );

  /**
   * The path above the folder.
   *
   * Read off the loaded tree when it is there, because that is instant. But a
   * folder reached any other way — opened from the file list, or from the
   * access map — is not in the loaded tree, and the path came back empty:
   * standing somewhere with no way back up. The server now sends the chain, so
   * the tree is the shortcut and the answer is never missing.
   */
  const breadcrumb = useMemo(() => {
    const fromTree = selectedId ? findPath(treeData, selectedId) : null;
    if (fromTree && fromTree.length) return fromTree;
    if (!detail) return [];
    return [...(detail.ancestors || []) as unknown as VaultFolder[], detail];
  }, [selectedId, treeData, findPath, detail]);

  /* The organization the path starts from. It is carried on the ROOT of the
     path — the root listing is what joins the tenant — not on the folder that
     happens to be open, which is why reading it off `detail` showed nothing. */
  const organization = breadcrumb[0]?.tenant_name || detail?.tenant_name || '';

  /* ── selected folder ── */
  const loadDetail = useCallback(
    async (folderId: string) => {
      setDetailLoading(true);
      try {
        const [folder, acc] = await Promise.all([
          vaultService.getFolder(folderId),
          vaultService.getAccess({ folder_id: folderId }),
        ]);
        setDetail(folder);
        setAccess(acc);
        setRollupOpen(false);
        return folder;
      } catch (e: any) {
        /* The load failed, so there is nothing to show — and leaving the LAST
           folder's contents on screen is how a refusal came to look like a
           folder that opened anyway. Clear it, and when the folder is simply
           gone, take its row out of the tree instead of leaving something that
           will fail again on the next click. */
        const data = e?.response?.data || {};
        setDetail(null);
        setAccess(null);
        if (data.reason === 'gone' || e?.response?.status === 404) {
          setSelectedId(null);
          setTreeData((prev) => dropNode(prev, folderId));
          message.warning(data.error || tRef.current('vault.folderGone'));
        } else {
          message.error(data.error || tRef.current('vault.loadFailed'));
        }
        return null;
      } finally {
        setDetailLoading(false);
      }
    },
    [],
  );

  /**
   * While a summary is still being written, keep looking for it.
   *
   * The summaries land a few seconds after a file changes, on the server's own
   * schedule. Without this the page would sit on the copy it fetched when the
   * folder was opened, and a person would see "writing the summary…" forever —
   * the summary having quietly arrived in the database minutes ago. Clicking the
   * same folder again does not refetch, so waiting is not a workaround a person
   * can find on their own.
   *
   * It polls only while something is actually pending, quietly (no spinner), and
   * gives up after a few minutes so a file that genuinely cannot be described
   * does not leave a timer running for the rest of the session.
   *
   * Keyed on WHICH folder and WHETHER anything is pending — not on the folder
   * object. Depending on the object made every poll replace it, restart the
   * timer and re-render the workspace on a five-second heartbeat; and a poll
   * that finds nothing new now keeps the copy it already had, so the table does
   * not redraw for no reason.
   */
  const pollFolderId = detail?.folder_id;
  const summariesPending = (detail?.files || []).some((f) => !f.summary);

  useEffect(() => {
    if (!pollFolderId || !summariesPending) return undefined;

    let attempts = 0;
    const timer = window.setInterval(async () => {
      attempts += 1;
      if (attempts > SUMMARY_POLL_LIMIT) {
        window.clearInterval(timer);
        return;
      }
      try {
        const fresh = await vaultService.getFolder(pollFolderId);
        setDetail((current) => {
          // Guard against a late answer for a folder the person has left.
          if (current?.folder_id !== pollFolderId) return current;
          const before = current.files || [];
          const after = fresh.files || [];
          const unchanged = before.length === after.length
            && before.every((f, i) => f.summary === after[i]?.summary);
          return unchanged ? current : fresh;
        });
      } catch {
        // A folder deleted or a permission revoked mid-poll: stop asking.
        window.clearInterval(timer);
      }
    }, SUMMARY_POLL_MS);

    return () => window.clearInterval(timer);
  }, [pollFolderId, summariesPending]);

  /**
   * Open a folder, and make the tree show where you are.
   *
   * Selecting set the id and loaded the contents, which is enough when the
   * click came FROM the tree. It is not enough when it came from anywhere else
   * — the file list, the access map — because the folder's branch may not be
   * open, or may not even be loaded: the row highlights something nobody can
   * see, and the navigator still points at where you used to be. Walking down
   * from the root loads each level and opens it, so the tree always answers
   * "where am I".
   */
  const revealInTree = async (folder: VaultFolder) => {
    const chain = (folder.ancestors || []).map((a) => a.folder_id);
    if (!chain.length) return;
    if (findPath(treeData, folder.folder_id)) {
      // Already loaded — it just needs its branch open.
      setExpandedKeys((k) => Array.from(new Set([...k.map(String), ...chain])));
      return;
    }
    // Top down: a level cannot be patched into the tree until its parent is.
    for (const id of chain) {
      // eslint-disable-next-line no-await-in-loop
      await revealChildren(id);
    }
  };

  useEffect(() => {
    const target = deepLinkedFolder.current;
    if (!target) return;
    deepLinkedFolder.current = null;
    selectFolder(target);
    // Drop the param so a refresh shows the Library, not a replay.
    window.history.replaceState({}, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectFolder = async (folderId: string) => {
    setSelectedId(folderId);
    const folder = await loadDetail(folderId);
    if (folder) await revealInTree(folder);
  };

  /* Ask the server as you type. Two characters is the endpoint's own floor, and
     the wait keeps a search from firing once per keystroke. A result is opened
     through selectFolder, which walks the ancestors down from the root — so
     landing on a folder four levels deep shows you where it lives, rather than
     dropping you somewhere with no idea how you got there. */
  useEffect(() => {
    const q = treeQuery.trim();
    if (q.length < 2) {
      setSearchHits(null);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    let dropped = false;
    const timer = setTimeout(async () => {
      try {
        const hits = await vaultService.search(q);
        if (!dropped) setSearchHits(hits);
      } catch {
        // A failed search must not blank the navigator: leave the tree as it is
        // and let the next keystroke try again.
        if (!dropped) setSearchHits(null);
      } finally {
        if (!dropped) setSearching(false);
      }
    }, 250);
    return () => { dropped = true; clearTimeout(timer); };
  }, [treeQuery]);

  /**
   * Open something the access map named.
   *
   * A folder is simply selected. A file has no page of its own — it lives in a
   * folder — so we go to the folder and remember which file to open once its
   * contents have arrived; `pendingReveal` is picked up by the effect below.
   */
  const [pendingReveal, setPendingReveal] = useState<string | null>(null);

  /* A file straight from the map, no folder detour. Routing through the
     folder broke for exactly the people the map serves: a context's governor
     may VIEW a linked file while holding no rights on the folder around it —
     the detour then 403'd on the folder, told them the item was 'no longer in
     the library', and left the spinner running (operator report 2026-08-15).
     Ask for rights on the FILE, and open the viewer directly. */
  const [mapFile, setMapFile] = useState<{ row: VaultFile; access: VaultAccess } | null>(null);

  /** Open a file straight from the "Shared with me" list — same direct path
   *  the access map uses: rights on the FILE, no folder detour (the grantee
   *  usually holds nothing on the folder around it). */
  const openSharedFile = async (sf: SharedFileEntry) => {
    try {
      const acc = await vaultService.getAccess({ file_id: sf.file_id });
      if (!acc.can_view) {
        message.warning(t('vault.accessMap.cannotOpen'));
        return;
      }
      setMapFile({
        row: {
          file_id: sf.file_id, file_name: sf.file_name,
          file_display_name: sf.file_name,
        } as VaultFile,
        access: acc,
      });
    } catch {
      message.warning(t('vault.accessMap.cannotOpen'));
    }
  };

  const revealNode = async (node: {
    kind: 'folder' | 'file'; id: string; folder_id?: string | null; name?: string | null;
  }) => {
    if (node.kind === 'file') {
      try {
        const acc = await vaultService.getAccess({ file_id: node.id });
        if (!acc.can_view) {
          message.warning(t('vault.accessMap.cannotOpen'));
          return;
        }
        setAccessMapOpen(false);
        setMapFile({
          row: { file_id: node.id, file_name: node.name || '', file_display_name: node.name || '' } as VaultFile,
          access: acc,
        });
      } catch {
        message.warning(t('vault.accessMap.cannotOpen'));
      }
      return;
    }
    if (!node.id) {
      message.warning(t('vault.accessMap.cannotOpen'));
      return;
    }
    setAccessMapOpen(false);
    selectFolder(node.id);
  };

  useEffect(() => {
    if (!pendingReveal || !detail) return;
    const row = (detail.files || []).find((f) => f.file_id === pendingReveal);
    setPendingReveal(null);
    if (row) openFile(row);
    else message.warning(t('vault.accessMap.cannotOpen'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingReveal, detail]);

  const refreshDetailAndTree = async (folderId: string, parentToReload?: string | null) => {
    await loadDetail(folderId);
    if (parentToReload) {
      const parent = await vaultService.getFolder(parentToReload);
      setTreeData((prev) => patchChildren(prev, parentToReload, (parent.children || []).map((c) => toNode(c))));
    } else {
      loadRoots();
    }
  };

  /* ── delete confirmations state facts, not "are you sure" ── */
  useEffect(() => {
    setDeleteImpact(null);
    if (!confirmDelete) return undefined;
    let dropped = false;
    vaultService.folderDeleteImpact(confirmDelete.folder_id)
      .then((impact) => { if (!dropped) setDeleteImpact(impact); })
      .catch(() => { /* the dialog still confirms, just without the preview */ });
    return () => { dropped = true; };
  }, [confirmDelete]);

  useEffect(() => {
    setRemoveImpact(null);
    if (!confirmRemove || !detail) return undefined;
    let dropped = false;
    vaultService.fileDeleteImpact(detail.folder_id, confirmRemove.file_id)
      .then((impact) => { if (!dropped) setRemoveImpact(impact); })
      .catch(() => { /* same: confirm still works without the preview */ });
    return () => { dropped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmRemove]);

  /* The Organization folder's charter — who may add — shown to everyone
     standing in it, switchable by its admins. */
  useEffect(() => {
    if (!detail?.org_root) { setOrgSettings(null); return undefined; }
    let dropped = false;
    vaultService.getOrgSettings()
      .then((s) => { if (!dropped) setOrgSettings(s); })
      .catch(() => { /* banner falls back to the static line */ });
    return () => { dropped = true; };
  }, [detail?.org_root, detail?.folder_id]);

  /* ── moving things: shared aftermath ── */
  /** Refresh every surface a move touched: both branches, and the open folder. */
  const afterMove = async (p: DragPayload, destFolderId: string) => {
    if (p.kind === 'folder') {
      // Both branches and the roots are independent reads — one round trip,
      // not three, so the tree settles as fast as the move itself.
      await Promise.all([
        p.parentId ? revealChildren(p.parentId).catch(() => undefined) : undefined,
        revealChildren(destFolderId).catch(() => undefined),
        loadRoots(),
      ]);
      if (selectedId === p.folderId || selectedId === p.parentId
          || selectedId === destFolderId) {
        loadDetail(selectedId!);
      }
    } else {
      await revealChildren(destFolderId).catch(() => undefined);
      if (detail) loadDetail(detail.folder_id);
    }
  };

  /**
   * A drop landed on a folder. Ask the server who would gain and lose FIRST:
   * a silent move (nobody's reach changes) just happens, the way Drive feels —
   * anything with consequences opens the confirm dialog showing them.
   */
  const handleVaultDrop = async (
    p: DragPayload, target: VaultFolder, copy = false,
  ) => {
    // A COPY into the same folder is a legitimate act ("duplicate here"); a
    // MOVE into the folder it already sits in is not.
    if (!copy && p.folderId === target.folder_id) return;

    /* Ctrl/⌘-drag = copy. A copy grants nobody anything new — it is a new
       file in the destination — so there is no impact to preview and no
       dialog to open: it just happens, and says so. Only FILES copy; a
       folder copy is a different act with a different meaning, so a
       modifier-drag of a folder stays a move. */
    if (copy && p.kind === 'file' && p.fileId) {
      try {
        await vaultService.copyFile(p.folderId, p.fileId, {
          dest_folder_id: target.folder_id,
          new_display_name: t('vault.copy.copyOfName', { name: p.name }),
        });
        message.success(t('vault.copy.done', { name: p.name, dest: target.folder_name }));
        await revealChildren(target.folder_id).catch(() => undefined);
        if (detail) loadDetail(detail.folder_id);
      } catch (e: any) {
        message.error(e?.response?.data?.error || t('vault.actionFailed'));
      }
      return;
    }
    try {
      const impact = await vaultService.moveImpact({
        folder_id: p.folderId,
        ...(p.kind === 'file' ? { file_id: p.fileId } : {}),
        new_parent_id: target.folder_id,
      });
      if (impact.silent) {
        // Hand the answer to the dialog rather than making it ask again.
        setMoveState({
          source: p,
          presetDest: { folderId: target.folder_id, name: target.folder_name },
          presetImpact: impact,
        });
        return;
      }
      if (p.kind === 'file' && p.fileId) {
        await vaultService.moveFile(p.folderId, p.fileId, target.folder_id);
      } else {
        await vaultService.moveFolder(p.folderId, target.folder_id);
      }
      message.success(t('vault.move.done', { name: p.name, dest: target.folder_name }));
      afterMove(p, target.folder_id);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.actionFailed'));
    }
  };

  /* Which folder the pointer is currently over while dragging, and whether
     the modifier is held. Drive's contract, and people expect it: a plain
     drag MOVES, holding Ctrl/⌘ COPIES — and the cursor plus the drop target
     say which one is about to happen (2026-08-29). */
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [dragCopy, setDragCopy] = useState(false);

  /** Copy when the modifier is down — the same key every file manager uses. */
  const wantsCopy = (e: React.DragEvent) => e.ctrlKey || e.metaKey || e.altKey;

  /** Drag-source props for a row or a tree title. */
  const dragProps = (p: DragPayload) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.setData(VAULT_DND, JSON.stringify(p));
      // BOTH, so the modifier can pick at drop time; 'move' alone made the
      // browser refuse a copy however the keys were held.
      e.dataTransfer.effectAllowed = 'copyMove';
      setDragCopy(wantsCopy(e));
    },
    onDragEnd: () => { setDropTarget(null); setDragCopy(false); },
  });

  /** Drop-target props for a folder (tree title or table folder row). */
  const dropProps = (target: VaultFolder) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(VAULT_DND) || target.system_home) return;
      e.preventDefault();
      const copy = wantsCopy(e);
      e.dataTransfer.dropEffect = copy ? 'copy' : 'move';
      // Held down mid-drag, not only at pick-up: people decide to copy while
      // already dragging.
      setDragCopy(copy);
      setDropTarget(target.folder_id);
    },
    onDragEnter: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(VAULT_DND) || target.system_home) return;
      setDropTarget(target.folder_id);
    },
    onDragLeave: (e: React.DragEvent) => {
      // Leaving for a CHILD of this row is not leaving the row.
      if (e.currentTarget.contains(e.relatedTarget as Node)) return;
      setDropTarget((cur) => (cur === target.folder_id ? null : cur));
    },
    onDrop: (e: React.DragEvent) => {
      const raw = e.dataTransfer.getData(VAULT_DND);
      setDropTarget(null);
      const copy = wantsCopy(e);
      setDragCopy(false);
      if (!raw || target.system_home) return;
      e.preventDefault();
      e.stopPropagation();
      try {
        handleVaultDrop(JSON.parse(raw) as DragPayload, target, copy);
      } catch { /* not our payload */ }
    },
    'data-drop-active': dropTarget === target.folder_id ? (dragCopy ? 'copy' : 'move') : undefined,
  });

  /* ── folder actions ── */
  /* Naming happens in the tree now — see startDraftFolder. There is no dialog
     for it: a modal that slides in from the edge of the screen to collect one
     word is more ceremony than the act deserves. */
  /**
   * Re-read one folder's children and put them in the tree, open.
   *
   * The bug this replaces: creating a subfolder refreshed the children of the
   * PARENT of the folder you were standing in, so the folder that had actually
   * just gained a child was never repatched and the new folder stayed invisible
   * until a reload.
   */
  const revealChildren = async (folderId: string) => {
    const fresh = await vaultService.getFolder(folderId);
    setTreeData((prev) =>
      patchChildren(prev, folderId, (fresh.children || []).map((c) => toNode(c))));
    setExpandedKeys((k) => (k.includes(folderId) ? k : [...k, folderId]));
    return fresh;
  };

  /**
   * Start naming a folder in the tree.
   *
   * Not a modal. A modal for a single word is a lot of ceremony, and this one
   * animated in from the edge of the screen every time. Instead the folder
   * appears where it is going to live, named "New folder" with the text
   * selected: type over it and press Enter, or click away and keep the default.
   * Escape abandons it. Nothing is written until then.
   */
  /** Free space = anywhere in the navigator that is not a control or a real
   *  folder row. A section heading is a label, not a folder — it counts. */
  const isNavDeadSpace = (el: HTMLElement): boolean => {
    // A submenu flyout (New file → …, Upload files → …) is its own portal,
    // NOT inside `.ant-dropdown`, and React bubbles its click through the
    // portal to this panel — read as free space, it deselected the very
    // folder the pick was for (seen 2026-09-26: "From Google Drive" left
    // "Pick a folder" behind the picker).
    if (el.closest('input, button, a, .vault-search-hit, .ant-dropdown, .ant-dropdown-menu-submenu-popup')) return false;
    const row = el.closest('.ant-tree-treenode');
    if (row && !row.querySelector('.vault-nav-section')) return false;
    return true;
  };

  const startDraftFolder = (parentId: string | null) => {
    draftDone.current = false;
    setDraftFolder({ parentId, name: t('vault.newFolderName'), busy: false });
    if (parentId) setExpandedKeys((k) => (k.includes(parentId) ? k : [...k, parentId]));
  };

  const cancelDraftFolder = () => {
    draftDone.current = true;
    committing.current = false;
    setDraftFolder(null);
  };

  const commitDraftFolder = async () => {
    if (!draftFolder || draftFolder.busy || committing.current) return;
    committing.current = true;
    const name = draftFolder.name.trim();
    const { parentId } = draftFolder;
    if (!name) { committing.current = false; cancelDraftFolder(); return; }
    draftDone.current = true;
    setDraftFolder((d) => (d ? { ...d, busy: true } : d));
    try {
      const made = await vaultService.createFolder(name, parentId || undefined);
      setDraftFolder(null);
      if (parentId) await revealChildren(parentId);
      else await loadRoots();
      // Go to what was just made — the point of making it.
      selectFolder(made.folder_id);
      committing.current = false;
    } catch (e: any) {
      // Keep the row open with the name still in it, so a clash ("a folder
      // called that already exists here") is one keystroke from fixed rather
      // than something to start over.
      draftDone.current = false;
      committing.current = false;
      setDraftFolder((d) => (d ? { ...d, busy: false } : d));
      message.error(e?.response?.data?.error || t('vault.actionFailed'));
    }
  };

  const handleDeleteFolder = async (folder: VaultFolder) => {
    try {
      await vaultService.deleteFolder(folder.folder_id);
      message.success(t('vault.deleted'));
      const parent = folder.parent_folder_id;
      if (selectedId === folder.folder_id) { setDetail(null); setSelectedId(null); }
      if (parent) await revealChildren(parent);
      else await loadRoots();
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.actionFailed'));
    }
  };

  /* ── renaming, in place ──────────────────────────────────────
     Click the name of something already selected, or press F2. Enter commits,
     Escape abandons. After a successful rename the system says whether anything
     actually referred to the old name — see commitRename. */
  const startRename = (
    kind: 'folder' | 'file', id: string, current: string, where: 'tree' | 'title' = 'tree',
  ) => {
    setRenaming({ kind, id, original: current, where });
    setRenameValue(current);
  };

  const cancelRename = () => {
    setRenaming(null);
    setRenameValue('');
  };

  /**
   * Say what referred to the old name, in one line a person can act on.
   *
   * This is not a canned warning. Before the rename goes through, the server
   * searches every agent, skill and organization instruction in the tenant for
   * the CURRENT name and returns the ones that contain it. If the list is empty
   * the message says so plainly, which is the common case and worth saying —
   * silence would leave a person wondering.
   */
  const reportRenameImpact = (
    impact: RenameImpact | null, oldName: string, newName: string,
  ) => {
    if (!impact) return;

    if (impact.mentions.length === 0) {
      message.success(t('vault.renameNote.clean', { name: newName }), 6);
      return;
    }
    const named = impact.mentions.slice(0, 4).map((m) => m.name).join(', ');
    const more = impact.mentions.length > 4
      ? t('vault.renameNote.andMore', { n: impact.mentions.length - 4 })
      : '';
    notification.warning({
      message: t('vault.renameNote.foundTitle', { n: impact.mentions.length }),
      description: t('vault.renameNote.foundBody', {
        old: oldName, named: `${named}${more ? ` ${more}` : ''}`,
      }),
      duration: 0,
      placement: mirrorPlacement(direction, 'bottomRight'),
    });
  };

  const commitRename = async () => {
    if (!renaming) return;
    const next = renameValue.trim();
    const { kind, id, original } = renaming;
    if (!next || next === original) { cancelRename(); return; }

    setRenameBusy(true);
    try {
      // Ask BEFORE renaming. The search is for the name as it is written in
      // instructions right now — once the rename lands, that string is gone and
      // the same question would truthfully answer "nothing mentions it".
      const node = kind === 'folder' ? { folder_id: id } : { file_id: id };
      const impact = await vaultService.renameImpact(node).catch(() => null);
      if (kind === 'folder') {
        await vaultService.renameFolder(id, next);
      } else {
        if (!detail) return;
        await vaultService.renameFile(detail.folder_id, id, next);
      }
      cancelRename();
      if (kind === 'folder' && detail) {
        refreshDetailAndTree(detail.folder_id, detail.parent_folder_id);
      } else if (detail) {
        loadDetail(detail.folder_id);
      }
      reportRenameImpact(impact, original, next);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.actionFailed'));
    } finally {
      setRenameBusy(false);
    }
  };

  /** The inline editor itself — one component for folders and files. */
  const renameInput = (
    <input dir="auto"
      className="vault-rename-input"
      value={renameValue}
      autoFocus
      disabled={renameBusy}
      maxLength={255}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setRenameValue(e.target.value)}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') commitRename();
        if (e.key === 'Escape') cancelRename();
      }}
      // Clicking away commits, the way a desktop does. Abandoning is Escape.
      onBlur={() => commitRename()}
    />
  );

  /**
   * The row for a folder that does not exist yet.
   *
   * Its name is selected the moment it appears, so typing replaces it. Enter
   * commits, clicking away commits (keeping "New folder" if nothing was typed),
   * Escape throws it away. `draftDone` stops the blur that FOLLOWS Enter or
   * Escape from committing a second time.
   */
  const draftInput = (
    <span className="vault-tree-node is-draft">
      <FolderIcon size={15} />
      <input dir="auto"
        className="vault-rename-input"
        value={draftFolder?.name ?? ''}
        autoFocus
        disabled={!!draftFolder?.busy}
        maxLength={255}
        onFocus={(e) => e.currentTarget.select()}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraftFolder((d) => (d ? { ...d, name: e.target.value } : d))}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') commitDraftFolder();
          if (e.key === 'Escape') cancelDraftFolder();
        }}
        /* Clicking away SHOULD commit, the way a desktop does. It does not
           yet: the draft row is re-created as the tree re-renders, the input
           loses focus, blur fires, and the commit re-renders the tree — a loop
           React catches and complains about. Enter commits and Escape abandons,
           which is honest; the blur gesture is held back until the row has a
           stable identity rather than shipped as something that can spin. */
        onBlur={() => { if (!draftDone.current) cancelDraftFolder(); }}
      />
    </span>
  );

  /**
   * The tree as drawn, with the folder being named spliced in where it will
   * live. It is deliberately NOT in `treeData`: a row that does not exist on
   * the server has no business in the state everything else reads.
   */
  const treeWithDraft = useMemo(() => {
    const q = treeQuery.trim().toLowerCase();
    // The ORGANIZATION section, rolled up: its heading stays and moves to the
    // foot of the tree, its folders come out. A view preference and nothing
    // more — the folders keep existing, keep their contents and keep every
    // grant on them. Done HERE rather than in loadRoots so the toggle is
    // instant and costs no round trip, and so nothing about what the server
    // returned is thrown away.
    const visible = orgCollapsed
      ? [
        ...treeData.filter((n) => n.section !== 'org' && !n.folder?.org_root),
        // Last, so a closed section sits out of the way of the folders being
        // worked in rather than holding its place in the middle of them.
        ...treeData.filter((n) => n.section === 'org'),
      ]
      : treeData;
    const base = q
      // A branch survives if it matches, or if anything inside it does — the
      // way a search through folders has to work, or you can only ever find
      // what happens to be at the top.
      ? (function keep(nodes: TreeNodeData[]): TreeNodeData[] {
        return nodes.reduce<TreeNodeData[]>((out, n) => {
          const kids = n.children ? keep(n.children as TreeNodeData[]) : [];
          const label = n.sharedFile
            ? n.sharedFile.file_name
            : (n.folder?.folder_name || '');
          const hit = label.toLowerCase().includes(q);
          if (hit || kids.length) out.push({ ...n, children: kids.length ? kids : n.children });
          return out;
        }, []);
      }(visible))
      : visible;
    if (!draftFolder) return base;
    const row = DRAFT_ROW;
    if (!draftFolder.parentId) {
      // A new top-level folder is YOURS: the draft row must appear inside
      // the "My files" group, not dangle after the organization section at
      // the foot of the tree (operator report 2026-09-07). Insert it before
      // the first section header that is not 'mine'; with no such header
      // (single group or empty tree) the end IS the my-files area.
      const firstHeader = base.findIndex((n) => !!n.section);
      if (firstHeader !== -1 && base[firstHeader].section !== 'mine') {
        // No "My files" group yet — the draft opens it, above the others.
        return [row, ...base];
      }
      const cut = base.findIndex((n, i) => n.section && n.section !== 'mine' && i > 0);
      if (cut === -1) return [...base, row];
      return [...base.slice(0, cut), row, ...base.slice(cut)];
    }
    const inject = (nodes: TreeNodeData[]): TreeNodeData[] => nodes.map((n) => {
      if (n.key === draftFolder.parentId) {
        return { ...n, children: [...((n.children as TreeNodeData[]) || []), row] };
      }
      return n.children ? { ...n, children: inject(n.children as TreeNodeData[]) } : n;
    });
    return inject(base);
  }, [treeData, draftFolder, treeQuery, orgCollapsed]);

  /** A tree row: its name, or an editor when this folder is being renamed. */
  const renderTreeTitle = (node: TreeNodeData) => {
    if (node.key === DRAFT_KEY) return draftInput;
    // Section headers are labels between groups, not folders: no menu, no
    // tooltip, no drag — just where a group of roots begins.
    if (node.section) {
      return (
        <span className="vault-nav-section">
          {t(`vault.section.${node.section}`)}
          {/* The eye lives ON the section it acts on. Collapsing is a view,
              not a permission: the folders keep existing, keep their contents
              and keep every grant — the heading simply stops unrolling them,
              and steps to the foot of the tree while it is closed, out of the
              way of the folders being worked in. */}
          {node.section === 'org' && (
            <Tooltip
              title={orgCollapsed
                ? t('vault.showOrgFolders')
                : t('vault.hideOrgFolders')}
              mouseEnterDelay={0.2}
              placement={mirrorPlacement(direction, 'right')}
            >
              <button
                type="button"
                className={`vault-org-toggle${orgCollapsed ? ' is-off' : ''}`}
                aria-pressed={orgCollapsed}
                aria-label={orgCollapsed
                  ? t('vault.showOrgFolders')
                  : t('vault.hideOrgFolders')}
                onClick={(e) => {
                  // The heading is a tree row; without this the click would
                  // also reach the tree and try to select a non-folder.
                  e.stopPropagation();
                  setOrgCollapsed((v) => !v);
                }}
              >
                {orgCollapsed ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </Tooltip>
          )}
        </span>
      );
    }
    // A single FILE someone shared with me: it has no folder to select —
    // clicking it opens the viewer directly (see the onSelect intercept).
    if (node.sharedFile) {
      const sf = node.sharedFile;
      const kind = fileKind(sf.file_name, sf.file_type || undefined);
      return (
        <Tooltip
          title={sf.shared_by_person
            ? t('vault.sharedFileByPerson', { person: sf.shared_by_person })
            : sf.file_name}
          mouseEnterDelay={0.4}
          placement={endPlacement(direction)}
        >
          <span dir="auto" className="vault-tree-node vault-tree-file">
            <span className={`vault-file-icon ${kind}`}>{FILE_GLYPH[kind]}</span>
            <span className="vault-tree-text">
              <span className="vault-tree-label" dir="auto">{sf.file_name}</span>
            </span>
            <span className="vault-shared-mark" aria-hidden="true"><SharedInIcon /></span>
          </span>
        </Tooltip>
      );
    }
    const f = node.folder;
    if (renaming?.kind === 'folder' && renaming.id === f.folder_id
        && renaming.where === 'tree') {
      return <span dir="auto" className="vault-tree-node">{renameInput}</span>;
    }
    // Rights for THIS folder, which on a right-click is usually not the one
    // open. Fetched when the menu opens; until it answers the menu offers
    // nothing, rather than offering something that will be refused.
    const acc = menuAccess[f.folder_id] || (f.folder_id === selectedId ? access : null);
    // Anchors stay put: the organization root and the system homes never
    // drag. Everything else can be picked up, and every folder that is not a
    // system home can be dropped on.
    const movable = !f.org_root && !f.system_home;
    return (
      <Dropdown
        trigger={['contextMenu']}
        overlayClassName="vault-ctx-menu"
        menu={{ items: folderMenuItems(f, acc), onClick: onFolderMenuClick(f) }}
        onOpenChange={(open) => { if (open) ensureMenuAccess(f.folder_id); }}
      >
      <span>
      <Tooltip
        title={node.tenantLabel && f.tenant_name
          ? `${f.folder_name} · ${f.tenant_name}` : f.folder_name}
        mouseEnterDelay={0.6}
        placement={endPlacement(direction)}
      >
        <span
          className={`vault-tree-node${f.org_root ? ' vault-org-root' : ''}`}
          {...(movable ? dragProps({
            kind: 'folder', folderId: f.folder_id, name: f.folder_name,
            parentId: f.parent_folder_id,
          }) : {})}
          {...dropProps(f)}
        >
          {f.org_root ? <OrgFolderIcon size={15} /> : <FolderIcon size={15} />}
          <span className="vault-tree-text">
            {/* Renaming is right-click → Rename, the way a PC file explorer
                does it (operator decision 2026-08-15). A name that renames
                itself when clicked kept catching people who only meant to
                select the folder. */}
            {/* No organization sub-label under the name (operator decision
                2026-08-19): the folder's NAME does the talking, and rights
                decide who may use it regardless of whose tenant it sits in.
                Cross-tenant managers still see "name · organization" in the
                hover tooltip when several tenants are on screen. */}
            <span className="vault-tree-label" dir="auto">
              {f.folder_name}
            </span>
          </span>
          {/* A word took a third of the row and said only "shared". An icon
              says the same and leaves room for the name; hovering says who to
              go and ask, which is the thing you actually want to know. */}
          {f.shared_with_me && (
            <span
              className="vault-shared-mark"
              aria-label={t('vault.sharedWithMe')}
              /* A plain title attribute, not a second antd Tooltip: the row
                 already has one, and nesting them put two bubbles on screen at
                 once, overlapping. */
              title={f.shared_by_person
                ? t('vault.sharedByPerson', {
                    person: f.shared_by_person, org: f.shared_by || '',
                  })
                : t('vault.sharedByOrg', { org: f.shared_by || '' })}
            >
              <SharedInIcon />
            </span>
          )}
          {/* No "System" badge (operator decision 2026-08-19): migrated
              bundles and homes are real folders people actually use, and a
              machinery label on them read as clutter. The flag still quietly
              gates what is OFFERED — homes get no move/delete/drag. */}
        </span>
      </Tooltip>
      </span>
      </Dropdown>
    );
  };

  /* ── uploads / files ── */
  /* The Library's ceilings, so the browser can refuse before uploading. Null
     until they arrive (and if they never do, every file is simply sent and the
     server answers) — a limit we could not read must not become a limit of
     zero. */
  const [limits, setLimits] = useState<VaultLimits | null>(null);
  useEffect(() => {
    vaultService.limits().then(setLimits).catch(() => setLimits(null));
  }, []);

  /** The folder a dropped file should land in, making the tree as it goes.
   *
   *  A folder upload arrives as a flat list of files, each carrying the path it
   *  had on disk (`webkitRelativePath`). Rebuilding that shape is the whole
   *  point — flattening 200 files out of a nested folder into one heap is not
   *  "uploading the folder", it is losing it — so each path segment becomes a
   *  real folder, reusing one that is already there rather than failing on the
   *  duplicate-name rule.
   *
   *  `made` is shared across the batch so a hundred files from one folder cost
   *  one folder lookup rather than a hundred.
   */
  const folderForPath = async (
    rootId: string, segments: string[], made: Map<string, string>,
  ): Promise<string> => {
    let parent = rootId;
    let walked = '';
    for (const segment of segments) {
      walked = walked ? `${walked}/${segment}` : segment;
      const seen = made.get(walked);
      if (seen) { parent = seen; continue; }
      const holder = await vaultService.getFolder(parent);
      const existing = (holder.children || []).find((c) => c.folder_name === segment);
      const id = existing
        ? existing.folder_id
        : (await vaultService.createFolder(segment, parent)).folder_id;
      made.set(walked, id);
      parent = id;
    }
    return parent;
  };

  /** A size as a person says it, so a refusal reads like a sentence. */
  const humanBytes = (n: number) => (
    n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(1)} GB`
      : n >= 1024 ** 2 ? `${Math.round(n / 1024 ** 2)} MB`
        : `${Math.max(1, Math.round(n / 1024))} KB`);

  /** Why this file cannot be uploaded, or null if it can.
   *
   *  Checked in the browser purely so a hopeless file fails NOW instead of
   *  after minutes of uploading; the server checks again and has the last word.
   */
  const whyNot = (f: File): string | null => {
    if (!limits) return null;
    const ext = `.${(f.name.split('.').pop() || '').toLowerCase()}`;
    if (!limits.allowed_extensions.includes(ext)) {
      return t('vault.upload.badTypeShort', { list: limits.allowed_extensions.join(' ') });
    }
    if (f.size > limits.max_file_bytes) {
      return t('vault.upload.tooBigShort', {
        size: humanBytes(f.size), max: humanBytes(limits.max_file_bytes),
      });
    }
    return null;
  };

  const handleUpload = async (fileList: FileList, folderId?: string) => {
    const target = folderId || uploadTarget.current || detail?.folder_id;
    uploadTarget.current = null;
    if (!target) return;

    const all = Array.from(fileList);
    if (!all.length) return;

    // One batch at a time. A second batch replaced the tray's list wholesale,
    // so the first one's rows vanished while its uploads were still running —
    // and since a row's id is its position in its own batch, a later
    // completion could mark a row belonging to the other batch, showing "done"
    // for a file that had failed. The tray's promise is that you can look away
    // and still learn what happened; two batches sharing it breaks exactly
    // that.
    if (uploadingRef.current) {
      message.warning(t('vault.upload.oneAtATime'));
      return;
    }
    uploadingRef.current = true;

    // Every file appears in the list AT ONCE, before any of them is sent, so
    // the size of the job is visible from the first moment rather than being
    // revealed one row at a time.
    const queued: UploadItem[] = all.map((f, i) => {
      const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath || '';
      const segments = rel ? rel.split('/').slice(0, -1) : [];
      const refusal = whyNot(f);
      return {
        id: `${i}-${f.name}`,
        name: f.name,
        path: segments.join('/'),
        status: refusal ? 'skipped' : 'queued',
        reason: refusal || undefined,
      };
    });
    setUploadItems(queued);

    const mark = (id: string, patch: Partial<UploadItem>) =>
      setUploadItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

    setUploading(true);
    setUploadPct(0);
    const made = new Map<string, string>();
    const touched = new Set<string>([target]);

    // One at a time, on purpose: a person watching wants to see the count move
    // predictably, and the server's quota is checked per upload — ten parallel
    // requests could each be told there is room and collectively not fit.
    for (let i = 0; i < all.length; i += 1) {
      const file = all[i];
      const item = queued[i];
      if (item.status === 'skipped') continue;
      mark(item.id, { status: 'uploading' });
      try {
        const segments = item.path ? item.path.split('/') : [];
        const dest = segments.length
          ? await folderForPath(target, segments, made)
          : target;
        touched.add(dest);
        const { outcome } = await vaultService.uploadToFolder(dest, file, setUploadPct);
        mark(item.id, { status: outcome === 'replaced' ? 'replaced' : 'done' });
      } catch (e: any) {
        // The reason lands on the row itself. A failure that only ever existed
        // as a toast was a failure most people never saw.
        mark(item.id, {
          status: 'failed',
          reason: e?.response?.data?.error || t('vault.uploadFailed'),
        });
      }
    }

    uploadingRef.current = false;
    setUploading(false);
    setUploadPct(undefined);
    await loadDetail(detail && touched.has(detail.folder_id) ? detail.folder_id : target);
    // The folder tree gained rows if a folder upload made any.
    if (made.size) void loadRoots();
  };

  /** Copy the picked Drive files into the target folder.
   *
   *  The same tray, the same rows, the same outcomes as a local upload — to
   *  the person these ARE uploads, only the bytes come from Drive instead of
   *  a disk. One call per file, on purpose: a row turns green the moment its
   *  own file lands, and a slow export never holds the whole batch in one
   *  request. A refused type reads on its row exactly as the local upload
   *  would say it; a dead connection marks every row with the composer's
   *  reconnect message rather than a generic failure.
   */
  const handleDriveUpload = async (picked: DriveFileRef[]) => {
    const target = uploadTarget.current || detail?.folder_id;
    uploadTarget.current = null;
    if (!target || !picked.length) return;
    if (uploadingRef.current) {
      message.warning(t('vault.upload.oneAtATime'));
      return;
    }
    uploadingRef.current = true;

    const queued: UploadItem[] = picked.map((f, i) => ({
      id: `${i}-${f.file_id}`, name: f.name, path: '', status: 'queued',
    }));
    setUploadItems(queued);
    const mark = (id: string, patch: Partial<UploadItem>) =>
      setUploadItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

    setUploading(true);
    for (let i = 0; i < picked.length; i += 1) {
      const item = queued[i];
      mark(item.id, { status: 'uploading' });
      try {
        const [result] = await vaultService.uploadFromDrive(target, [picked[i].file_id]);
        if (result?.status === 201) {
          // A Google Doc lands as "Plan.docx" — show the name it now has here.
          mark(item.id, {
            status: result.outcome === 'replaced' ? 'replaced' : 'done',
            name: result.placement?.file_display_name || item.name,
          });
        } else {
          mark(item.id, { status: 'failed', reason: result?.error || t('vault.uploadFailed') });
        }
      } catch (e: any) {
        const data = e?.response?.data || {};
        const reason = data.error_code === 'CLOUD_NOT_CONNECTED'
          ? t('common.upload.errors.CLOUD_NOT_CONNECTED')
          : (data.error || t('vault.uploadFailed'));
        mark(item.id, { status: 'failed', reason });
        // Nothing after this can be fetched either: say so on every row at
        // once instead of asking Drive once per file for the same answer.
        if (data.error_code === 'CLOUD_NOT_CONNECTED') {
          queued.slice(i + 1).forEach((rest) => mark(rest.id, { status: 'failed', reason }));
          break;
        }
      }
    }

    uploadingRef.current = false;
    setUploading(false);
    await loadDetail(target);
  };

  const handleRemoveFile = async (fileId: string) => {
    if (!detail) return;
    try {
      await vaultService.removeFile(detail.folder_id, fileId);
      message.success(t('vault.fileRemoved'));
      loadDetail(detail.folder_id);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.actionFailed'));
    }
  };

  /* ── drag-anywhere upload over the workspace ── */
  const canDropHere = !!(detail && access?.can_edit);

  const onDragEnter = (e: React.DragEvent) => {
    if (!canDropHere || !e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragOver = (e: React.DragEvent) => {
    if (canDropHere && e.dataTransfer?.types?.includes('Files')) e.preventDefault();
  };
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  const onDrop = (e: React.DragEvent) => {
    if (!canDropHere) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (e.dataTransfer.files?.length) handleUpload(e.dataTransfer.files);
  };

  /* ── derived ── */
  /* 'owner' is full rights WITHOUT the manager role: your own folder. It
     read as plain 'Editor' before, which made an owner believe they had lost
     their sharing and delete rights when they had all of them. */
  /* What the chip beside the folder name says you may do here.
     
     It used to be a four-rung ladder — manager / owner / editor / viewer —
     which cannot describe three independent rights. Anyone holding edit AND
     share-on but not delete landed on "editor" and read "Can edit", with no
     sign anywhere that they could also pass the folder on. Someone shares a
     folder WITH the share right and then cannot see that they have it (field
     report 2026-08-29).
     
     So: a manager gets the one word that says it, and everyone else gets one
     chip per right they actually hold, in the same vocabulary as the share
     drawer's own checkboxes. Eight combinations, eight truthful readings. */
  const accessChips: { key: string; label: string; hint: string; tone: string }[] = (() => {
    if (!access) return [];
    if (access.is_manager) {
      return [{ key: 'manager', tone: 'manager',
                label: t('vault.accessManager'), hint: t('vault.accessManagerHint') }];
    }
    const held: { key: string; label: string; hint: string; tone: string }[] = [];
    if (access.can_edit) {
      held.push({ key: 'edit', tone: 'editor',
                  label: t('vault.accessEditor'), hint: t('vault.accessEditorHint') });
    }
    if (access.can_delete) {
      held.push({ key: 'delete', tone: 'editor',
                  label: t('vault.accessDelete'), hint: t('vault.accessDeleteHint') });
    }
    if (access.can_reshare) {
      held.push({ key: 'reshare', tone: 'editor',
                  label: t('vault.accessReshare'), hint: t('vault.accessReshareHint') });
    }
    return held.length ? held : [{
      key: 'viewer', tone: 'viewer',
      label: t('vault.accessViewer'), hint: t('vault.accessViewerHint'),
    }];
  })();

  const fmtDate = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString(activeLocale(), { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const fmtSize = (mb?: number | null) => {
    if (mb == null) return '—';
    if (mb < 0.1) return `${Math.max(1, Math.round(mb * 1024))} KB`;
    return `${mb.toFixed(1)} MB`;
  };

  /**
   * What the folder contains, as one list.
   *
   * A file manager shows you the folders you can walk into next to the files
   * you can open; splitting them across two panels means the tree is the only
   * way down, and on a narrow window the tree is the first thing to go.
   * Subfolders come first, as they do everywhere else.
   */
  type TableRow = VaultFile & { __folder?: VaultFolder };
  const tableRows: TableRow[] = useMemo(() => [
    ...((detail?.children || []).map((c) => ({
      __folder: c,
      file_id: `folder:${c.folder_id}`,
      vault_file_id: `folder:${c.folder_id}`,
      file_display_name: c.folder_name,
    }) as unknown as TableRow)),
    ...(((detail?.files || []) as TableRow[])),
  ], [detail]);

  /** What a row is called — a folder's name, or the file's name here. */
  const rowName = (row: TableRow) => (row.__folder
    ? row.__folder.folder_name : (row.file_display_name || row.file_name || ''));

  const shownRows = useMemo(() => {
    const q = fileQuery.trim().toLowerCase();
    if (!q) return tableRows;
    return tableRows.filter((r) => rowName(r).toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRows, fileQuery]);

  /* Whether THIS folder is already reachable by someone else — every child
     inherits that. Read off what the folder listing already carries rather than
     asked for over the network: one more request per folder, answered by an
     effect, is one more thing that can loop. */
  const folderIsShared = !!detail?.shared_with_me || (detail?.share_count ?? 0) > 0;

  /* ── multi-select ─────────────────────────────────────────────────────
     Drive's and the Finder's selection: a checkbox per row and one for the
     page, Ctrl/⌘-click toggles a row, Shift-click takes the range from the
     last row touched, Esc lets go. A bar above the table carries what can be
     done to ALL of them — asked of the server first, item by item, so an
     action is offered only when every selected item allows it, and a
     disabled one says which items stop it and why. Keys are the table's own
     row keys (`vault_file_id`, `folder:<id>` for a subfolder). */
  const [selKeys, setSelKeys] = useState<string[]>([]);
  const selAnchor = useRef<string | null>(null);
  /* What the table is sorted by, so a Shift-click range follows the order on
     screen rather than the order the folder arrived in. */
  const [sortState, setSortState] = useState<{ key?: string; order?: 'ascend' | 'descend' | null }>({});
  const [bulkRules, setBulkRules] = useState<Record<string, BulkCheckItem> | null>(null);
  const [bulkDialog, setBulkDialog] = useState<BulkVerb | null>(null);
  /* Bumped after a write, so the same selection is asked about again. */
  const [bulkCheckRound, setBulkCheckRound] = useState(0);
  const [bulkDeleteImpact, setBulkDeleteImpact] = useState<BulkDeleteImpact | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const clearSelection = () => { setSelKeys([]); selAnchor.current = null; };

  // A selection belongs to the folder it was made in…
  useEffect(() => { clearSelection(); }, [detail?.folder_id]);
  // …and only to rows that are still there after the folder reloads.
  useEffect(() => {
    setSelKeys((prev) => {
      const live = new Set(tableRows.map((r) => r.vault_file_id));
      const kept = prev.filter((k) => live.has(k));
      return kept.length === prev.length ? prev : kept;
    });
  }, [tableRows]);

  /* The server names a file by its PLACEMENT — this folder plus its id —
     because the same file can be filed in several folders. */
  const selectedRows = useMemo(() => {
    const picked = new Set(selKeys);
    return tableRows.filter((r) => picked.has(r.vault_file_id));
  }, [selKeys, tableRows]);
  const selectedItems: BulkItem[] = useMemo(() => selectedRows.map((row) => (row.__folder
    ? { kind: 'folder' as const, folder_id: row.__folder.folder_id }
    : { kind: 'file' as const, folder_id: detail?.folder_id || '', file_id: row.file_id })),
  [selectedRows, detail?.folder_id]);
  const serverKey = (i: BulkItem) => (i.kind === 'folder'
    ? `folder:${i.folder_id}` : `file:${i.folder_id}:${i.file_id}`);
  /* The same items, with an identity that changes only when WHICH items are
     selected does — a folder reload hands back new row objects for the same
     selection, and an open picker must not rebuild itself for that. */
  const selectionKey = selectedItems.map(serverKey).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableItems = useMemo(() => selectedItems, [selectionKey]);

  /* Ask what the selection allows. Debounced, because a Shift-click range or
     a select-all changes the selection several times in one gesture. Keyed on
     WHICH items are selected — the folder reloading (a summary landing every
     few seconds) hands back new row objects for the same selection, and that
     must not blank the bar and ask again. */
  useEffect(() => {
    setBulkRules(null);
    if (!selectedItems.length) return undefined;
    let dropped = false;
    const timer = window.setTimeout(async () => {
      try {
        const rows = await vaultService.bulkCheck(selectedItems);
        if (!dropped) setBulkRules(Object.fromEntries(rows.map((r) => [r.key, r])));
      } catch {
        // Nothing is offered on a check that failed; the tooltip says so.
        if (!dropped) setBulkRules({});
      }
    }, 150);
    return () => { dropped = true; window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey, bulkCheckRound]);

  /** A right-click anywhere on a row, anchored at the pointer. */
  const [rowMenu, setRowMenu] = useState<{ x: number; y: number; row: TableRow } | null>(null);

  /* The name a press has ARMED for renaming — files and folders alike.
     Windows and the Finder both work this way: press a name once and nothing
     happens but a selection, press it again and it becomes editable. A single
     click that opened an editor made renaming something people did by accident
     on the way to opening a row.

     Nothing listens on `document` to disarm it: an armed name is simply
     forgotten when another name is pressed, when a rename starts, or when the
     folder changes. A global click listener was the first shape of this and it
     cancelled the very press that armed it — React's stopPropagation holds back
     the synthetic event, not the native one still travelling to the document. */

  /* A menu opened at a point has no trigger to close it: antd closes on its own
     trigger's blur, and this one has none. So it sat there while the page
     scrolled out from under it. Any click, any scroll, Escape — it goes. */
  useEffect(() => {
    if (!rowMenu) return;
    const close = () => setRowMenu(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    // A frame later, or the very click that opened it would close it again.
    const id = window.setTimeout(() => document.addEventListener('click', close), 0);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('click', close);
    };
  }, [rowMenu]);

  /* Highlighting a row that is scrolled out of sight is the same as not
     highlighting it. Runs after the branch has opened. */
  useEffect(() => {
    if (!selectedId) return;
    const id = window.setTimeout(() => {
      document.querySelector('.vault-nav-tree .ant-tree-treenode-selected')
        ?.scrollIntoView({ block: 'nearest' });
    }, 260);
    return () => window.clearTimeout(id);
  }, [selectedId, expandedKeys]);

  /** One number for both kinds of row: a file's size, or a folder's contents. */
  const rowSizeMb = (row: TableRow) => (row.__folder
    ? (row.__folder.total_size_bytes || 0) / (1024 * 1024)
    : (row.file_size_mb || 0));

  /** Who a row belongs to, and whether that is the signed-in person. */
  const ownerOf = (row: TableRow): { name: string | null; mine: boolean } => {
    const id = row.__folder ? row.__folder.owned_by_user_id : row.uploaded_by_user_id;
    const name = row.__folder
      ? (row.__folder as { owned_by_name?: string | null }).owned_by_name || null
      : (row as { uploaded_by_name?: string | null }).uploaded_by_name || null;
    const mine = !!id && !!user?.user_id && id === user.user_id;
    return { name: mine ? (user?.user_full_name || name) : name, mine };
  };

  /* The room the file table really has — the panel, not the viewport. A
     callback ref, because the files area mounts and unmounts with the folder
     (and its loading state); a ResizeObserver follows the navigator's drag. */
  const [filesWidth, setFilesWidth] = useState(0);
  const filesObserver = useRef<ResizeObserver | null>(null);
  const filesRef = useCallback((el: HTMLDivElement | null) => {
    filesObserver.current?.disconnect();
    filesObserver.current = null;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setFilesWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(el);
    filesObserver.current = observer;
  }, []);
  const columnPlan = useMemo(() => planFileColumns(filesWidth), [filesWidth]);

  const columns = useMemo(
    () => [
      {
        title: t('vault.fileName'),
        key: 'name',
        // The table is laid out FIXED (below): every column has its width
        // before content is measured, so a long name ellipsises inside its
        // cell instead of pushing the row's menu off the panel. The name has
        // priority — see planFileColumns: a floor, the larger share, and the
        // whole remainder once the summary has given way.
        width: columnPlan.nameWidth,
        sortDirections: ['ascend' as const, 'descend' as const],
        sorter: (a: TableRow, b: TableRow) =>
          rowName(a).localeCompare(rowName(b), undefined, { sensitivity: 'base', numeric: true }),
        render: (_: unknown, row: TableRow) => {
          if (row.__folder) {
            const f = row.__folder;
            const shared = f.shared_with_me || (f.share_count ?? 0) > 0 || folderIsShared;
            const editingFolder = renaming?.kind === 'folder' && renaming.id === f.folder_id
              && renaming.where === 'title';
            return (
              <div className="vault-file-name">
                <div className="vault-file-icon vault-file-glyph"><FolderIcon size={18} /></div>
                <div className="vault-file-title">
                  {editingFolder ? renameInput : (
                    <span className="fname" dir="auto" title={f.folder_name}>{f.folder_name}</span>
                  )}
                  <span className="fmeta">
                    {t('vault.containsFiles', { n: f.total_file_count ?? f.file_count ?? 0 })}
                  </span>
                </div>
                {/* Said, not left to be inferred: "no badge" reads equally as
                    "private" and as "nobody has got round to sharing it". */}
                <Tooltip
                  title={f.shared_onto_shelf
                    ? t('vault.sharedOntoShelf', { person: f.shared_by_person || '' })
                    : (shared
                        ? (f.shared_with_me
                            ? t('vault.sharedByOrg', { org: f.shared_by || '' })
                            : t('vault.sharedOutHint'))
                        : t('vault.privateHint'))}
                >
                  <span className={shared ? 'vault-shared-mark' : 'vault-private-mark'}>
                    {shared ? <SharedInIcon /> : <OnlyYouIcon />}
                  </span>
                </Tooltip>
              </div>
            );
          }
          const kind = fileKind(row.file_display_name || row.file_name, row.file_type);
          const shown = row.file_display_name || row.file_name || '';
          const editing = renaming?.kind === 'file' && renaming.id === row.file_id;
          return (
            <div className="vault-file-name">
              <div className={`vault-file-icon ${kind}`}>{FILE_GLYPH[kind]}</div>
              <div className="vault-file-title">
                {editing ? renameInput : (
                  <span className="vault-file-title-line">
                    {/* The full name on hover — the cell cuts long ones. */}
                    <span dir="auto" className="fname" title={shown}>{shown}</span>
                    <VaultDriveCopyBadge file={row} />
                  </span>
                )}

              </div>
            </div>
          );
        },
      },
      {
        /* Who this belongs to — the column every drive has, and the one the
           Library was missing: a shared folder full of rows gave no clue who
           put each one there (2026-08-29). "me" for your own, the person's
           name otherwise, with the same initials coin the rest of the app
           uses. */
        title: t('vault.ownerCol'),
        key: 'owner',
        width: COL_W.owner,
        responsive: ['md' as const],
        sortDirections: ['ascend' as const, 'descend' as const],
        sorter: (a: TableRow, b: TableRow) =>
          (ownerOf(a).name || '').localeCompare(ownerOf(b).name || '',
            undefined, { sensitivity: 'base' }),
        render: (_: unknown, row: TableRow) => {
          const o = ownerOf(row);
          if (!o.name) return <span className="vault-cell-muted">—</span>;
          return (
            <span className="vault-owner-cell">
              <span className="vault-owner-coin" aria-hidden="true" dir="auto">
                {(o.name.trim()[0] || '?').toUpperCase()}
              </span>
              <span dir="auto" className="vault-owner-name">{o.mine ? t('vault.ownerMe') : o.name}</span>
            </span>
          );
        },
      },
      {
        title: t('vault.lastModified'),
        key: 'modified',
        width: COL_W.modified,
        /* Three states, the way every drive does it: ascending, descending,
           then back to the order the folder actually has. */
        sortDirections: ['ascend' as const, 'descend' as const],
        sorter: (a: TableRow, b: TableRow) =>
          new Date((a.__folder ? a.__folder.updated_at : a.created_at) || 0).getTime()
          - new Date((b.__folder ? b.__folder.updated_at : b.created_at) || 0).getTime(),
        // Its own column now: a date wedged under the name competed with the
        // name for the one thing that has to be readable at any width.
        responsive: ['md' as const],
        render: (_: unknown, row: TableRow) => (
          <span className="vault-cell-muted">
            {fmtDate(row.__folder ? (row.__folder.updated_at || row.__folder.created_at)
                                  : row.created_at)}
          </span>
        ),
      },
      {
        title: t('vault.size'),
        key: 'size',
        width: COL_W.size,
        responsive: ['md' as const],
        sortDirections: ['ascend' as const, 'descend' as const],
        // A folder has no size of its own; it sorts as nothing rather than
        // pretending to a number.
        // A folder is sized by everything inside it, so the two kinds of row
        // are comparable and sorting by size means one thing.
        sorter: (a: TableRow, b: TableRow) => rowSizeMb(a) - rowSizeMb(b),
        render: (_: unknown, row: TableRow) => (
          <span className="vault-cell-muted">
            {row.__folder && !row.__folder.total_file_count
              ? '—'
              : fmtSize(rowSizeMb(row))}
          </span>
        ),
      },
      {
        title: t('vault.summary'),
        key: 'summary',
        // No width: the summary takes what the other columns leave, and is
        // the first thing to go when the window narrows. What must survive
        // is the name and the buttons — losing those leaves a list you can
        // read and cannot act on.
        responsive: ['lg' as const],
        render: (_: unknown, row: TableRow) => {
          if (row.__folder) {
            const r = row.__folder.rollup_summary;
            return r
              ? (
                <Tooltip title={r} placement={mirrorPlacement(direction, 'topLeft')}>
                  <div className="vault-summary-cell bidi-clamp" dir="auto">{r}</div>
                </Tooltip>
              )
              : <span className="vault-draft-badge">{t('vault.openFolderHint')}</span>;
          }
          if (row.summary) {
            return (
              <Tooltip title={row.summary} placement={mirrorPlacement(direction, 'topLeft')}>
                <div className="vault-summary-cell bidi-clamp" dir="auto">{row.summary}</div>
              </Tooltip>
            );
          }
          // "Writing the summary…" is a promise. Keep it only where it is true.
          // A file whose processing failed is never going to get one, and saying
          // otherwise leaves a person waiting on something that is not coming.
          if (row.processing_status === 'failed') {
            return (
              <Tooltip title={t('vault.summaryFailedHint')}>
                <span className="vault-draft-badge failed">{t('vault.summaryFailed')}</span>
              </Tooltip>
            );
          }
          /* Seconds, not a chore: the summary is already being written. */
          return <span className="vault-draft-badge">{t('vault.summaryPending')}</span>;
        },
      },
      {
        /* One kebab instead of a strip of icons — the drive idiom, and it
           carries the SAME menu the right-click already opens, so there is
           one list of actions to learn and one place to maintain them. */
        title: '',
        key: 'actions',
        width: COL_W.actions,
        render: (_: unknown, row: TableRow) => (
          <div className="vault-row-actions" onClick={(e) => e.stopPropagation()}>
            <Dropdown
              trigger={['click']}
              overlayClassName="vault-ctx-menu"
              menu={row.__folder
                ? {
                    items: folderMenuItems(row.__folder, menuAccess[row.__folder.folder_id] || access),
                    onClick: (info) => onFolderMenuClick(row.__folder!)?.(info),
                  }
                : { items: fileMenuItems(), onClick: (info) => onFileMenuClick(row)?.(info) }}
            >
              <button
                type="button"
                className="vault-icon-btn vault-row-kebab"
                aria-label={t('vault.rowMenu')}
                aria-haspopup="menu"
              >
                <KebabIcon />
              </button>
            </Dropdown>
          </div>
        ),
      },
    ].filter((c) => !columnPlan.hidden.has(c.key as DroppableColumn)),
    // `renaming` and its value belong here: the name cell turns into an editor
    // while a rename is in progress, and a memo that did not know about it kept
    // rendering the old, static name. `armedName` belongs for the same reason
    // and cost an afternoon: without it every cell closed over the value the
    // name had when the memo last ran — always null — so the first press armed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [access, t, language, renaming, renameValue, renameBusy, folderIsShared,
     user, menuAccess, columnPlan],
  );


  // Root folder creation is a tenant-management act (manager-only server-side).
  const canCreateRoot = hasRole('admin');
  const canCreateHere = !!(detail && access?.can_edit);
  const showNewButton = canCreateRoot || canCreateHere;

  /**
   * The kinds of file a person can start from nothing.
   *
   * Named for what they ARE, with the extension after — somebody wanting a
   * spreadsheet should not have to know it is called xlsx here.
   */
  const NEW_FILE_KINDS = [
    { kind: 'document', label: t('vault.kind.document'), ext: '.docx' },
    { kind: 'sheet', label: t('vault.kind.sheet'), ext: '.xlsx' },
    { kind: 'deck', label: t('vault.kind.deck'), ext: '.pptx' },
    { kind: 'markdown', label: t('vault.kind.markdown'), ext: '.md' },
    { kind: 'text', label: t('vault.kind.text'), ext: '.txt' },
    { kind: 'csv', label: t('vault.kind.csv'), ext: '.csv' },
  ] as const;

  /** Rights for a folder that is not necessarily the one on screen. */
  const ensureMenuAccess = async (folderId: string) => {
    if (menuAccess[folderId]) return;
    try {
      const acc = await vaultService.getAccess({ folder_id: folderId });
      setMenuAccess((prev) => ({ ...prev, [folderId]: acc }));
    } catch { /* the menu simply shows nothing it cannot offer */ }
  };

  const handleNewFile = async (folderId: string, kind: typeof NEW_FILE_KINDS[number]['kind']) => {
    try {
      const made = await vaultService.createBlankFile(folderId, kind);
      message.success(t('vault.fileCreated', { name: made.file_display_name }));
      const fresh = await loadDetail(folderId);
      if (selectedId !== folderId) selectFolder(folderId);
      // Straight into the editor: nobody creates an empty document in order to
      // look at it.
      const row = (fresh?.files || []).find((f) => f.file_id === made.file_id);
      if (row) openFile(row, true);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.actionFailed'));
    }
  };

  /**
   * Everything you can do to one folder, in one place.
   *
   * Shared by the "New" button and by the right-click menu, so the two can
   * never drift apart — and gated on the rights for THAT folder, which for a
   * right-click is usually not the folder currently open.
   */
  const folderMenuItems = (folder: VaultFolder | null, acc?: VaultAccess | null): MenuProps['items'] => {
    const canEdit = !!acc?.can_edit;
    const canDelete = !!acc?.can_delete;
    const canSee = !!acc?.can_view;
    // No folder open = the workspace itself. The first offer is then a new
    // TOP-LEVEL folder (enabled), not a greyed-out subfolder pointing at
    // nothing — the file-explorer expectation.
    if (!folder) {
      return [
        {
          key: 'root',
          icon: <FolderPlusIcon />,
          label: t('vault.newRootFolder'),
          disabled: !canCreateRoot,
        },
      ];
    }
    return [
      {
        key: 'folder',
        icon: <FolderPlusIcon />,
        label: t('vault.newSubfolder'),
        disabled: !canEdit,
      },
      {
        key: 'newfile',
        icon: <FilePlusIcon />,
        label: t('vault.newFile'),
        disabled: !canEdit,
        children: NEW_FILE_KINDS.map((k) => ({
          key: `newfile:${k.kind}`,
          label: (
            <span className="vault-kind-item">
              {k.label}<span className="vault-kind-ext">{k.ext}</span>
            </span>
          ),
        })),
      },
      {
        key: 'upload',
        icon: <UploadIcon />,
        label: t('vault.uploadFiles'),
        disabled: !folder || !canEdit,
        // With Drive connected the one entry opens into two: this computer,
        // or a copy out of Drive. Without it there are no children at all,
        // so the row stays the plain click-to-pick it has always been.
        ...(driveConnected
          ? {
              // The flyout is its own overlay; give it the menu's row styling.
              popupClassName: 'vault-new-menu',
              children: [
                { key: 'upload:computer', icon: <UploadIcon />, label: t('vault.upload.fromComputer') },
                { key: 'upload:drive', icon: <GoogleDriveGlyph size={15} />, label: t('vault.upload.fromDrive') },
              ],
            }
          : {}),
      },
      {
        key: 'upload-folder',
        icon: <FolderPlusIcon />,
        label: t('vault.uploadFolder'),
        disabled: !folder || !canEdit,
      },
      { type: 'divider' as const },
      {
        key: 'rename',
        icon: <PencilIcon />,
        label: t('vault.rename'),
        disabled: !folder || !canEdit,
      },
      // The anchors — the organization root and system homes — are not
      // OFFERED a move, rather than offering one the server will refuse.
      ...(folder && !folder.org_root && !folder.system_home
        ? [{
            key: 'move',
            icon: <MoveIcon />,
            label: t('vault.move.moveTo'),
            disabled: !canEdit,
          }]
        : []),
      {
        key: 'share',
        icon: <SharePersonIcon />,
        label: canSee && !acc?.is_manager && !acc?.can_reshare
          ? t('vault.share.whoHasAccess') : t('vault.share.share'),
        disabled: !folder || !canSee,
      },
      {
        key: 'download',
        icon: <DownloadIcon />,
        label: t('vault.download.folder'),
        disabled: !folder || !canSee,
      },
      {
        key: 'delete',
        icon: <TrashIcon />,
        danger: true,
        label: t('vault.delete'),
        disabled: !folder || !canDelete,
      },
      ...(canCreateRoot
        ? [{ type: 'divider' as const },
           { key: 'root', icon: <FolderIcon />, label: t('vault.newRootFolder') }]
        : []),
    ];
  };

  /** One handler for both menus. `folder` is whatever the menu was opened on. */
  const onFolderMenuClick = (folder: VaultFolder | null): MenuProps['onClick'] => ({ key }) => {
    if (key === 'root') { startDraftFolder(null); return; }
    if (!folder) return;
    if (key === 'folder') startDraftFolder(folder.folder_id);
    else if (key.startsWith('newfile:')) handleNewFile(folder.folder_id, key.slice(8) as any);
    else if (key === 'upload' || key === 'upload:computer') { uploadTarget.current = folder.folder_id; fileInputRef.current?.click(); }
    else if (key === 'upload:drive') { uploadTarget.current = folder.folder_id; setDrivePickerOpen(true); }
    else if (key === 'upload-folder') { uploadTarget.current = folder.folder_id; folderInputRef.current?.click(); }
    else if (key === 'rename') startRename('folder', folder.folder_id, folder.folder_name);
    else if (key === 'move') {
      setMoveState({
        source: {
          kind: 'folder', name: folder.folder_name,
          folderId: folder.folder_id, parentId: folder.parent_folder_id,
        },
      });
    } else if (key === 'share') setShareNode({ folder_id: folder.folder_id, name: folder.folder_name });
    else if (key === 'download') handleDownloadFolder(folder);
    else if (key === 'delete') setConfirmDelete(folder);
  };

  /**
   * A file's actions, as a menu.
   *
   * The row already carries these as buttons — and the buttons are the first
   * thing to disappear when the window is narrow, which left no way at all to
   * open, edit or share anything. A right-click reaches them at any width.
   */
  const fileMenuItems = (): MenuProps['items'] => [
    { key: 'open', icon: <EyeIcon />, label: t('vault.viewFile') },
    ...(access?.can_edit
      ? [{ key: 'edit', icon: <PencilIcon />, label: t('vault.editFile') }] : []),
    { key: 'share', icon: <SharePersonIcon />, label: t('vault.share.share') },
    // A copy needs only the right to READ this file — the write happens in
    // the destination folder, and the dialog only offers folders for that.
    { key: 'copy', icon: <CopyIcon />, label: t('vault.copy.makeCopy') },
    // The third gesture beside Move and Copy: the SAME file, shown in
    // another folder. A second doorway to one real file — an edit anywhere
    // changes it everywhere (operator request 2026-08-26).
    { key: 'place', icon: <FolderPlusIcon />, label: t('vault.place.addToFolder') },
    // Downloading is READING — it needs no right beyond the one that already
    // put the file on screen, so it sits above the divider with the other
    // read-only gestures.
    { key: 'download', icon: <DownloadIcon />, label: t('vault.download.file') },
    ...(access?.can_edit
      ? [{ type: 'divider' as const },
         { key: 'move', icon: <MoveIcon />, label: t('vault.move.moveTo') },
         { key: 'rename', icon: <PencilIcon />, label: t('vault.rename') },
         { key: 'remove', icon: <TrashIcon />, danger: true, label: t('vault.remove') }]
      : []),
  ];

  /**
   * Make a copy. When the person can write HERE, the copy lands beside the
   * original at once (the Drive gesture). When they cannot — a view-only
   * folder, a personal home that refuses copies — the destination dialog
   * opens instead of a refusal.
   */
  const handleCopyFile = async (row: VaultFile) => {
    if (!detail) return;
    const shown = row.file_display_name || row.file_name || '';
    const source: MoveSource = {
      kind: 'file', name: shown, folderId: detail.folder_id, fileId: row.file_id,
    };
    if (!access?.can_edit) {
      setMoveState({ source, mode: 'copy' });
      return;
    }
    try {
      await vaultService.copyFile(detail.folder_id, row.file_id, {
        new_display_name: t('vault.copy.copyOfName', { name: shown }),
      });
      message.success(t('vault.copy.doneHere', { name: shown }));
      loadDetail(detail.folder_id);
    } catch (e: any) {
      // A REFUSAL (403 — e.g. a personal home, which never takes copies) is
      // not a failure: offer a folder that will take it. Anything else is a
      // real error and must say so rather than hide behind a picker.
      if (e?.response?.status === 403) {
        setMoveState({ source, mode: 'copy' });
        return;
      }
      message.error(e?.response?.data?.error || t('vault.actionFailed'));
    }
  };

  /**
   * Save a file, or a folder as a .zip — the drive gesture.
   *
   * The bytes are fetched through the API client so the request carries the
   * caller's token and tenant header; a bare <a href> would arrive
   * unauthenticated and be refused. The Blob is handed to a throwaway anchor
   * and the object URL revoked straight after, so a long session does not
   * accumulate one per download.
   */
  const handleDownloadFile = async (row: VaultFile) => {
    const shown = row.file_display_name || row.file_name || 'file';
    try {
      const blob = await vaultService.downloadFile(row.file_id);
      triggerBlobDownload(blob, shown);
    } catch (e: any) {
      message.error((await blobErrorMessage(e)) || t('vault.download.failed'));
    }
  };

  const handleDownloadFolder = async (folder: VaultFolder) => {
    // A large folder is read, zipped and sent in one request, so this is the
    // one action here that can take a visible while. Say it is happening.
    const hide = message.loading(
      t('vault.download.preparing', { name: folder.folder_name }), 0);
    try {
      const blob = await vaultService.downloadFolder(folder.folder_id);
      triggerBlobDownload(blob, `${folder.folder_name}.zip`);
    } catch (e: any) {
      message.error((await blobErrorMessage(e)) || t('vault.download.failed'));
    } finally {
      hide();
    }
  };

  const onFileMenuClick = (row: VaultFile): MenuProps['onClick'] => ({ key }) => {
    const shown = row.file_display_name || row.file_name || '';
    if (key === 'open') openFile(row);
    else if (key === 'edit') openFile(row, true);
    else if (key === 'share') setShareNode({ file_id: row.file_id, name: shown });
    else if (key === 'copy') handleCopyFile(row);
    else if (key === 'download') handleDownloadFile(row);
    else if (key === 'place' && detail) {
      setMoveState({
        source: { kind: 'file', name: shown, folderId: detail.folder_id, fileId: row.file_id },
        mode: 'place',
      });
    }
    else if (key === 'move' && detail) {
      setMoveState({
        source: { kind: 'file', name: shown, folderId: detail.folder_id, fileId: row.file_id },
      });
    } else if (key === 'rename') startRename('file', row.file_id, shown);
    else if (key === 'remove') setConfirmRemove(row);
  };

  /* ── acting on the selection ── */
  /** Up to three names, quoted, and a count for the rest. */
  const quoteNames = (names: string[]) => {
    const shown = names.slice(0, 3).map((n) => t('vault.bulk.quoted', { name: n })).join(', ');
    return names.length > 3
      ? t('vault.bulk.namesMore', { names: shown, n: names.length - 3 }) : shown;
  };

  const BLOCK_REASONS: BulkBlockReason[] = [
    'no_view', 'no_edit', 'no_delete', 'no_reshare', 'org_root', 'system_home',
    'pinned_file', 'folder_copy', 'not_found',
  ];
  const reasonText = (reason: BulkBlockReason | 'unknown', names: string[]) => {
    const p = { names: quoteNames(names) };
    switch (reason) {
      case 'no_view': return t('vault.bulk.why.noView', p);
      case 'no_edit': return t('vault.bulk.why.noEdit', p);
      case 'no_delete': return t('vault.bulk.why.noDelete', p);
      case 'no_reshare': return t('vault.bulk.why.noReshare', p);
      case 'org_root': return t('vault.bulk.why.orgRoot', p);
      case 'system_home': return t('vault.bulk.why.systemHome', p);
      case 'pinned_file': return t('vault.bulk.why.pinnedFile', p);
      case 'folder_copy': return t('vault.bulk.why.folderCopy', p);
      case 'not_found': return t('vault.bulk.why.notFound', p);
      default: return t('vault.bulk.why.unknown');
    }
  };

  /** Why each action is not offered for the selection — null when every
   *  item allows it. Grouped by reason, so a sentence names the items it is
   *  about rather than repeating itself once per item. One pass over the
   *  selection answers all five buttons. */
  const BULK_ACTIONS: BulkAction[] = ['move', 'copy', 'delete', 'download', 'share'];
  const bulkBlocks: Record<BulkAction, string | null> = (() => {
    const none = { move: null, copy: null, delete: null, download: null, share: null };
    if (!selectedItems.length) return none;
    if (!bulkRules) {
      const checking = t('vault.bulk.checking');
      return { move: checking, copy: checking, delete: checking, download: checking, share: checking };
    }
    const groups = new Map<BulkAction, Map<BulkBlockReason | 'unknown', string[]>>(
      BULK_ACTIONS.map((a) => [a, new Map()]));
    selectedItems.forEach((item, i) => {
      const rule = bulkRules[serverKey(item)];
      BULK_ACTIONS.forEach((action) => {
        const reason = rule ? rule.actions[action] : 'unknown';
        if (!reason) return;
        const byReason = groups.get(action)!;
        if (!byReason.has(reason)) byReason.set(reason, []);
        byReason.get(reason)!.push(rowName(selectedRows[i]));
      });
    });
    const out: Record<BulkAction, string | null> = none;
    groups.forEach((byReason, action) => {
      out[action] = byReason.size ? Array.from(byReason.entries()).slice(0, 2)
        .map(([reason, names]) => reasonText(reason, names)).join(' ') : null;
    });
    return out;
  })();
  const bulkDone = (verb: BulkVerb, count: number) => ({
    move: () => t('vault.bulk.done.move', { count }),
    copy: () => t('vault.bulk.done.copy', { count }),
    delete: () => t('vault.bulk.done.delete', { count }),
    share: () => t('vault.bulk.done.share', { count }),
  })[verb]();
  const bulkPartial = (verb: BulkVerb, ok: number, total: number) => ({
    move: () => t('vault.bulk.partial.move', { ok, total }),
    copy: () => t('vault.bulk.partial.copy', { ok, total }),
    delete: () => t('vault.bulk.partial.delete', { ok, total }),
    share: () => t('vault.bulk.partial.share', { ok, total }),
  })[verb]();
  const bulkNone = (verb: BulkVerb, total: number) => ({
    move: () => t('vault.bulk.none.move', { total }),
    copy: () => t('vault.bulk.none.copy', { total }),
    delete: () => t('vault.bulk.none.delete', { total }),
    share: () => t('vault.bulk.none.share', { total }),
  })[verb]();

  /** A collision the server named with a code, in the reader's language;
   *  null when there is no code — the server's own sentence is used then. */
  const conflictText = (code?: string | null): string | null => {
    switch (code) {
      case 'into_itself': return t('vault.bulk.conflict.intoItself');
      case 'other_org': return t('vault.bulk.conflict.otherOrg');
      case 'already_there': return t('vault.bulk.conflict.alreadyThere');
      case 'name_taken': return t('vault.bulk.conflict.nameTaken');
      case 'content_missing': return t('vault.bulk.conflict.contentMissing');
      case 'quota': return t('vault.bulk.conflict.quota');
      default: return null;
    }
  };

  /**
   * Say what happened, item by item where it matters: "7 moved, 1 not
   * allowed: “Budget.xlsx”". A clean run is one passing line; anything less
   * stays on screen until it is read, naming what did not happen.
   */
  const reportBulk = (verb: BulkVerb, outcome: BulkOutcome) => {
    const total = outcome.results.length;
    const ok = outcome.summary.ok || 0;
    if (ok === total) {
      message.success(bulkDone(verb, ok));
      return;
    }
    const denied = outcome.results.filter((r) => r.status === 'denied');
    const failed = outcome.results.filter((r) => r.status !== 'ok' && r.status !== 'denied');
    const lines: string[] = [];
    if (denied.length) {
      const byReason = new Map<string, string[]>();
      denied.forEach((r) => {
        const code = r.code || '';
        if (!byReason.has(code)) byReason.set(code, []);
        byReason.get(code)!.push(r.name);
      });
      byReason.forEach((names, code) => {
        lines.push(BLOCK_REASONS.includes(code as BulkBlockReason)
          ? reasonText(code as BulkBlockReason, names)
          : t('vault.bulk.report.notAllowed', { count: names.length, names: quoteNames(names) }));
      });
    }
    if (failed.length) {
      lines.push(t('vault.bulk.report.failed', {
        count: failed.length,
        items: failed.slice(0, 4).map((r) => {
          const why = conflictText(r.code) || r.error;
          return why
            ? `${t('vault.bulk.quoted', { name: r.name })} — ${why}`
            : t('vault.bulk.quoted', { name: r.name });
        }).join('; '),
      }));
    }
    notification.warning({
      message: ok ? bulkPartial(verb, ok, total) : bulkNone(verb, total),
      description: (
        <div className="vault-bulk-report">
          {lines.map((line) => <p key={line}>{line}</p>)}
        </div>
      ),
      duration: 0,
      placement: mirrorPlacement(direction, 'bottomRight'),
    });
  };

  /** What is still selected after a run: the items that did not go — so
   *  "try those again somewhere else" is one click, not a re-selection. */
  const keepUnfinished = (outcome: BulkOutcome) => {
    const rowKeyOf = new Map(selectedItems.map((item, i) => [serverKey(item), selectedRows[i].vault_file_id]));
    setSelKeys(outcome.results.filter((r) => r.status !== 'ok')
      .map((r) => rowKeyOf.get(r.key)).filter((k): k is string => !!k));
  };

  /** Refresh what a bulk run touched: this folder, its branch, the destination. */
  const afterBulk = async (destId?: string) => {
    if (!detail) return;
    const here = detail.folder_id;
    setBulkCheckRound((n) => n + 1);
    await Promise.all([
      loadDetail(here),
      revealChildren(here).catch(() => undefined),
      destId && destId !== here ? revealChildren(destId).catch(() => undefined) : undefined,
    ]);
  };

  const handleBulkDownload = async () => {
    if (!detail || !selectedRows.length) return;
    // One item is the single download it always was — its own name, and a
    // folder's own zip.
    if (selectedRows.length === 1) {
      const only = selectedRows[0];
      if (only.__folder) handleDownloadFolder(only.__folder);
      else handleDownloadFile(only);
      return;
    }
    const name = t('vault.bulk.zipName', { name: detail.folder_name, n: selectedRows.length });
    const hide = message.loading(t('vault.bulk.preparing', { n: selectedRows.length }), 0);
    try {
      const blob = await vaultService.bulkDownload(selectedItems, name);
      triggerBlobDownload(blob, `${name}.zip`);
    } catch (e: any) {
      message.error(e?.response?.status === 429
        ? t('vault.bulk.rateLimited')
        : (await blobErrorMessage(e)) || t('vault.download.failed'));
    } finally {
      hide();
    }
  };

  /* The delete confirmation states facts for the whole selection, like the
     single ones do — asked for when it opens. */
  useEffect(() => {
    setBulkDeleteImpact(null);
    if (bulkDialog !== 'delete' || !selectedItems.length) return undefined;
    let dropped = false;
    vaultService.bulkDeleteImpact(selectedItems)
      .then((impact) => { if (!dropped) setBulkDeleteImpact(impact); })
      .catch(() => { /* the dialog still confirms, just without the preview */ });
    return () => { dropped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkDialog]);

  const runBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const outcome = await vaultService.bulkDelete(selectedItems);
      setBulkDialog(null);
      reportBulk('delete', outcome);
      keepUnfinished(outcome);
      await afterBulk();
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.actionFailed'));
    } finally {
      setBulkBusy(false);
    }
  };

  /** Shift-click: everything between the last row touched and this one, in
   *  the order the table shows them (its sort, not the folder's order). */
  const orderedKeys = (): string[] => {
    const col = columns.find((c) => c.key === sortState.key) as
      { sorter?: (a: TableRow, b: TableRow) => number } | undefined;
    const rows = [...shownRows];
    if (col?.sorter && sortState.order) {
      const sign = sortState.order === 'descend' ? -1 : 1;
      rows.sort((a, b) => sign * col.sorter!(a, b));
    }
    return rows.map((r) => r.vault_file_id);
  };

  const toggleRow = (key: string) => {
    selAnchor.current = key;
    setSelKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const selectRange = (key: string) => {
    const order = orderedKeys();
    const from = selAnchor.current ? order.indexOf(selAnchor.current) : -1;
    const to = order.indexOf(key);
    if (from < 0 || to < 0) { toggleRow(key); return; }
    const [a, b] = from < to ? [from, to] : [to, from];
    const range = order.slice(a, b + 1);
    setSelKeys((prev) => Array.from(new Set([...prev, ...range])));
  };

  /* Esc lets go of the selection — unless something on top of the page
     (a dialog, a drawer, an open menu, a field being typed in) is the thing
     Esc is meant for. */
  useEffect(() => {
    if (!selKeys.length) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const el = e.target as HTMLElement | null;
      if (el?.closest('input, textarea, [contenteditable="true"]')) return;
      if (document.querySelector(
        '.ant-modal-wrap:not([style*="display: none"]) .ant-modal, .ant-drawer-open, '
        + '.ant-dropdown:not(.ant-dropdown-hidden)',
      )) return;
      clearSelection();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selKeys.length]);

  const bulkButton = (
    action: BulkAction, icon: JSX.Element, label: string, onClick: () => void, danger = false,
  ) => {
    const why = bulkBlocks[action];
    return (
      <Tooltip key={action} title={why || label}>
        {/* A disabled button swallows the hover that would explain it. */}
        <span className="vault-bulk-btn-wrap">
          <Button
            size="small"
            icon={icon}
            danger={danger}
            disabled={!!why || bulkBusy}
            onClick={onClick}
            data-testid={`bulk-${action}`}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    );
  };

  const newMenuItems = folderMenuItems(detail, access);
  const onNewMenuClick = onFolderMenuClick(detail);

  return (
    <div className="admin-container">
      <Sidebar />
      <div className="main-container">
        <main className="main-content">
          <div className="vault-page">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files?.length) handleUpload(e.target.files);
                e.target.value = '';
              }}
            />
            {/* Its own input: `webkitdirectory` is set on the ELEMENT, and an
                element that has it can only pick folders — so offering both
                gestures takes two. */}
            <input
              /* `webkitdirectory` is set on the element rather than passed as a
                 prop: it is a real attribute in every browser we support, and
                 React's typings simply do not carry it. */
              ref={(el) => {
                folderInputRef.current = el;
                if (el) {
                  el.setAttribute('webkitdirectory', '');
                  el.setAttribute('directory', '');
                }
              }}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files?.length) handleUpload(e.target.files);
                e.target.value = '';
              }}
            />

            <VaultUploadTray items={uploadItems} onClose={() => setUploadItems([])} />
            {driveConnected && (
              <DriveFilePicker
                open={drivePickerOpen}
                alreadyAttached={[]}
                maxSelectable={DRIVE_PICKS_PER_BATCH}
                onAdd={(files) => { void handleDriveUpload(files); }}
                onClose={() => setDrivePickerOpen(false)}
              />
            )}

            {/* ── header ── */}
            <div className="vault-header">
              <div className="vault-header-title">
                <div className="vault-header-icon"><VaultMark /></div>
                <div>
                  <h1>{t('vault.title')}</h1>
                  <p className="vault-header-sub">{t('vault.subtitle')}</p>
                </div>
              </div>
              <div className="vault-header-actions">
                {/* The reverse of every other view here: not "who reaches this
                    folder" but "what does this agent, or this person, reach".
                    Everyone gets it — the server scopes it: managers see the
                    organization-wide table, everyone else their own slice
                    (their agents, their grants, their folders — nobody
                    else's shares). */}
                <button
                  type="button"
                  className="vault-map-btn"
                  onClick={() => setAccessMapOpen(true)}
                >
                  <PeopleMapIcon />
                  {t('vault.accessMapButton')}
                </button>
              </div>
            </div>

            {/* ── body ── */}
            <div className="vault-body">
              {/* navigator */}
              <div
                className="vault-panel vault-nav"
                style={{ width: navWidth }}
                /* Everything in this panel that is NOT a folder row or a
                   control is free space, the way a file explorer's white
                   area is: left-click deselects, right-click offers a new
                   TOP-LEVEL folder at the cursor (operator request
                   2026-09-07 — the strip under the tree alone was only
                   reachable after scrolling past the org sections). Section
                   headings ("My files") count as free space; real rows keep
                   their own menus. */
                onClick={(e) => {
                  if (!isNavDeadSpace(e.target as HTMLElement)) return;
                  setSelectedId(null); setDetail(null); setAccess(null);
                }}
                onContextMenu={(e) => {
                  if (!isNavDeadSpace(e.target as HTMLElement)) return;
                  e.preventDefault();
                  setBlankMenuAt({ x: e.clientX, y: e.clientY });
                }}
              >
                {blankMenuAt && (
                  <Dropdown
                    open
                    trigger={[]}
                    overlayClassName="vault-ctx-menu vault-blank-ctx"
                    onOpenChange={(open) => { if (!open) setBlankMenuAt(null); }}
                    menu={{
                      items: [{
                        key: 'root',
                        icon: <FolderPlusIcon />,
                        label: t('vault.newRootFolder'),
                        disabled: !canCreateRoot,
                      }],
                      onClick: () => { setBlankMenuAt(null); startDraftFolder(null); },
                    }}
                  >
                    <span
                      style={{
                        position: 'fixed', left: blankMenuAt.x, top: blankMenuAt.y,
                        width: 1, height: 1, pointerEvents: 'none',
                      }}
                    />
                  </Dropdown>
                )}
                {showNewButton && (
                  <div className="vault-new-wrap">
                    <Dropdown
                      menu={{ items: newMenuItems, onClick: onNewMenuClick }}
                      trigger={['click']}
                      placement={mirrorPlacement(direction, 'bottomLeft')}
                      overlayClassName="vault-new-menu"
                    >
                      <button type="button" className="vault-new-btn" aria-label={t('vault.new')}>
                        <span className="vault-new-plus"><PlusIcon /></span>
                        {t('vault.new')}
                        <CaretIcon />
                      </button>
                    </Dropdown>
                  </div>
                )}
                <div className="vault-nav-head">{t('vault.navigator')}</div>
                <div className="vault-nav-search">
                  <Input dir="auto"
                    allowClear
                    size="small"
                    value={treeQuery}
                    placeholder={t('vault.findFolder')}
                    onChange={(e) => setTreeQuery(e.target.value)}
                  />
                </div>
                <div className="vault-nav-tree">
                  {searchHits ? (
                    /* While searching, the navigator shows what was FOUND, not
                       the branches that happen to be open. Each row says which
                       folder it lives in, because a name on its own does not
                       tell you which of three "Drafts" you are looking at. */
                    <div className="vault-search-hits">
                      {searching && <div className="vault-search-note"><Spin size="small" /></div>}
                      {!searchHits.folders.length && !searchHits.files.length && !searching && (
                        <div className="vault-search-note">{t('vault.searchNoHits')}</div>
                      )}
                      {searchHits.folders.map((f) => (
                        <button
                          type="button"
                          key={`f-${f.folder_id}`}
                          className="vault-search-hit"
                          onClick={() => selectFolder(f.folder_id)}
                        >
                          <FolderIcon size={15} />
                          <span className="vault-search-hit-name" dir="auto">{f.folder_name}</span>
                          {f.tenant_name && <span className="vault-search-hit-where" dir="auto">{f.tenant_name}</span>}
                        </button>
                      ))}
                      {searchHits.files.map((f) => (
                        <button
                          type="button"
                          key={`v-${f.file_id}`}
                          className="vault-search-hit"
                          onClick={() => selectFolder(f.folder_id)}
                        >
                          <span className={`vault-file-icon ${fileKind(f.file_display_name)}`} dir="auto">
                            {FILE_GLYPH[fileKind(f.file_display_name)]}
                          </span>
                          <span className="vault-search-hit-name" dir="auto">{f.file_display_name}</span>
                          {f.folder_name && <span className="vault-search-hit-where" dir="auto">{f.folder_name}</span>}
                        </button>
                      ))}
                      {searchHits.capped && (
                        <div className="vault-search-note">{t('vault.searchCapped')}</div>
                      )}
                    </div>
                  ) : rootsLoading ? (
                    <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
                  ) : (treeData.length > 0 && treeWithDraft.length === 0) ? (
                    /* A query too short for the server search (< 2 chars) is
                       still applied as a LOCAL filter with no floor, so it can
                       empty a Library that is not empty. Saying nothing here
                       leaves a blank pane with no explanation — the state this
                       branch exists to name. Pre-existing on dev; cheap to
                       close while the guard below is being got right. */
                    <div className="vault-search-note">{t('vault.searchNoHits')}</div>
                  ) : (treeData.length === 0 && !draftFolder) ? (
                    /* Two questions, asked separately on purpose.
                       "Is the Library empty?" is `treeData` — server truth,
                       unfiltered. "What rows does the tree draw?" is
                       `treeWithDraft`, which also applies the search filter.
                       Guarding on the second was wrong in both directions:
                       New -> top folder adds only a DRAFT row, so an empty
                       Library kept this empty state mounted and the <Tree>
                       holding the draft never rendered (the button looked
                       dead); and a one-character query matching nothing
                       emptied the filtered list, so a user with 64 folders was
                       told they had none. `!draftFolder` covers the first,
                       `treeData` the second. */
                    <div className="vault-empty">
                      <EmptyFolderArt />
                      <h3>{t('vault.noFoldersTitle')}</h3>
                      <p>{t('vault.noFoldersBody')}</p>
                    </div>
                  ) : (
                    <Tree
                      treeData={treeWithDraft}
                      loadData={onLoadData as any}
                      expandedKeys={expandedKeys}
                      onExpand={(keys) => setExpandedKeys(keys)}
                      selectedKeys={selectedId ? [selectedId] : []}
                      onSelect={(keys, info) => {
                        // A shared FILE row opens the viewer — it is not a
                        // folder and must not become the "selected folder".
                        const clicked = info?.node as unknown as TreeNodeData;
                        if (clicked?.sharedFile) {
                          openSharedFile(clicked.sharedFile);
                          return;
                        }
                        const k = String(keys[0] || '');
                        if (k && k !== DRAFT_KEY) selectFolder(k);
                      }}
                      // Right-click selects the folder, the way a desktop does,
                      // and then the menu acts on it — so a menu can never be
                      // acting on a folder other than the one it is over.
                      onRightClick={({ node }) => {
                        // Only real folders have a menu — a section header,
                        // a shared-file leaf and the row being named do not.
                        const n = node as TreeNodeData;
                        if (n.section || n.sharedFile || n.key === DRAFT_KEY) return;
                        const key = String(n.key);
                        ensureMenuAccess(key);
                        if (key !== selectedId) selectFolder(key);
                      }}
                      titleRender={(node) => renderTreeTitle(node as TreeNodeData)}
                      blockNode
                    />
                  )}
                  {/* The empty space below the last folder answers a right-click
                      too — otherwise the only way to start a top-level folder is
                      to find one that already exists and right-click that.
                      It is a SIBLING of the rows, not a wrapper around them: a
                      wrapper caught every folder's right-click first and showed
                      the wrong menu. */}
                  {/* Spacer so free space exists even under a short tree;
                      the panel's own handlers make it act (deselect /
                      root-folder menu) like the rest of the dead space. */}
                  <div className="vault-nav-blank" />
                </div>

              </div>

              {/* Drag to resize the navigator. Also focusable and arrow-key
                  driven, because a person who cannot drag still needs to widen
                  a panel to read what is in it. */}
              <div
                className="vault-resizer"
                role="separator"
                aria-orientation="vertical"
                aria-label={t('vault.resizeNavigator')}
                aria-valuenow={navWidth}
                aria-valuemin={NAV_MIN}
                aria-valuemax={NAV_MAX}
                tabIndex={0}
                onPointerDown={startNavDrag}
                onDoubleClick={() => {
                  navWidthLive.current = NAV_DEFAULT;
                  setNavWidth(NAV_DEFAULT);
                  localStorage.setItem(NAV_WIDTH_KEY, String(NAV_DEFAULT));
                }}
                onKeyDown={(e) => {
                  const step = e.shiftKey ? 40 : 16;
                  const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
                  if (!dir) return;
                  e.preventDefault();
                  const rtl = document.documentElement.dir === 'rtl';
                  const next = Math.min(
                    NAV_MAX, Math.max(NAV_MIN, navWidth + dir * step * (rtl ? -1 : 1)),
                  );
                  navWidthLive.current = next;
                  setNavWidth(next);
                  localStorage.setItem(NAV_WIDTH_KEY, String(next));
                }}
              >
                <span className="vault-resizer-grip" aria-hidden="true" />
              </div>

              {/* workspace — the whole panel is a drop target for editors */}
              <div
                className="vault-panel vault-main"
                onDragEnter={onDragEnter}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
              >
                {dragging && detail && (
                  <div className="vault-drop-overlay">
                    <UploadIcon size={34} />
                    <span>{t('vault.dropToUpload', { name: detail.folder_name })}</span>
                  </div>
                )}
                {!selectedId ? (
                  <div className="vault-empty">
                    <EmptyFolderArt />
                    <h3>{t('vault.selectFolderTitle')}</h3>
                    <p>{t('vault.selectFolder')}</p>
                  </div>
                ) : detailLoading || !detail || !access ? (
                  <div className="vault-empty"><Spin size="large" /></div>
                ) : (
                  <>
                    <div className="vault-main-head">
                      {/* The path, always — a top-level folder has one too.
                          It used to appear only below a subfolder; on a folder
                          at the top the row showed the organization and not the
                          folder you were standing in, which on `get_folder`
                          (no tenant_name) meant it showed nothing at all. */}
                      <div className="vault-breadcrumb">
                        {organization && (
                          <span className="vault-crumb-part">
                            <span className="crumb org">{organization}</span>
                          </span>
                        )}
                        {breadcrumb.map((f, i) => (
                          <span key={f.folder_id} className="vault-crumb-part">
                            {(i > 0 || organization) && <span className="crumb-sep">/</span>}
                            <span
                              className={`crumb ${i === breadcrumb.length - 1 ? 'current' : ''}`}
                              onClick={() => i < breadcrumb.length - 1 && selectFolder(f.folder_id)}
                            >
                              {f.folder_name}
                            </span>
                          </span>
                        ))}
                      </div>

                      {detail.org_root && (
                        <div className="vault-org-banner">
                          <OrgFolderIcon size={14} />
                          {/* The charter, in one visible line: everyone can
                              look, and the switch says who can add. Drive's
                              Shared-Drive rules, stated where they apply
                              instead of implied by what fails. */}
                          <span>
                            {orgSettings
                              ? (orgSettings.members_can_add
                                  ? t('vault.orgCharterMembersAdd', { org: organization })
                                  : t('vault.orgCharterAdminsCurate', { org: organization }))
                              : t('vault.orgFolderBanner')}
                          </span>
                          {isManager && orgSettings && (
                            <span className="vault-org-switch">
                              <Switch
                                size="small"
                                checked={orgSettings.members_can_add}
                                onChange={async (on) => {
                                  try {
                                    const next = await vaultService.setMembersCanAdd(on);
                                    setOrgSettings(next);
                                    message.success(on
                                      ? t('vault.orgMembersCanAddOn')
                                      : t('vault.orgMembersCanAddOff'));
                                    // Rights on this folder just changed for
                                    // everyone — including possibly the viewer.
                                    loadDetail(detail.folder_id);
                                  } catch (e: any) {
                                    message.error(e?.response?.data?.error
                                      || t('vault.actionFailed'));
                                  }
                                }}
                              />
                              <span>{t('vault.orgMembersCanAdd')}</span>
                            </span>
                          )}
                        </div>
                      )}
                      <div className="vault-title-row">
                        <div className="vault-title-left">
                          {renaming?.kind === 'folder' && renaming.id === detail.folder_id
                            && renaming.where === 'title' ? (
                            <h2 className="vault-title-editing">{renameInput}</h2>
                          ) : (
                            <h2 dir="auto">{detail.folder_name}</h2>
                          )}
                          {accessChips.map((chip) => (
                            <Tooltip key={chip.key} title={chip.hint}>
                              <span className={`vault-access-chip ${chip.tone}`} dir="auto">
                                {chip.label}
                              </span>
                            </Tooltip>
                          ))}
                        </div>
                        <div className="vault-toolbar">
                          {/* Everyone who can see the folder can open this: a
                              manager to grant or revoke, anyone else to see who
                              already has access. The drawer itself hides the
                              controls a non-manager may not use. */}
                          {access.can_view && (
                            <Button
                              className="vault-share-btn"
                              icon={<SharePersonIcon />}
                              onClick={() =>
                                setShareNode({ folder_id: detail.folder_id, name: detail.folder_name })
                              }
                            >
                              {access.is_manager ? t('vault.share.share') : t('vault.share.whoHasAccess')}
                            </Button>
                          )}
                          {access.can_delete && (
                            <Tooltip title={t('vault.delete')}>
                              <button
                                type="button"
                                className="vault-icon-btn danger"
                                aria-label={t('vault.delete')}
                                onClick={() => setConfirmDelete(detail)}
                              >
                                <TrashIcon />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </div>

                      {uploading && (
                        <div className="vault-upload-progress">
                          <UploadIcon size={14} />
                          <Progress percent={uploadPct ?? 0} size="small" showInfo={false} />
                        </div>
                      )}
                    </div>

                    {/* Add-from-URL research → saves into THIS folder. Managers
                        only: the /web-research/research crawl endpoint is
                        admin-gated server-side, so showing the button to
                        non-managers would dead-end at a 403. */}
                    {isManager && access.can_edit && (
                      <div className="vault-url-research">
                        <UrlResearchSection
                          mode="folder"
                          folderId={detail.folder_id}
                          onFileSaved={() => loadDetail(detail.folder_id)}
                        />
                      </div>
                    )}

                    <div className="vault-files" ref={filesRef}>
                      {selKeys.length > 0 && (
                        <div className="vault-bulk-bar" role="toolbar"
                             aria-label={t('vault.bulk.toolbar')} data-testid="bulk-bar">
                          <Tooltip title={t('vault.bulk.clear')}>
                            <button type="button" className="vault-icon-btn vault-bulk-clear"
                                    aria-label={t('vault.bulk.clear')} onClick={clearSelection}>
                              <CloseIcon />
                            </button>
                          </Tooltip>
                          <span className="vault-bulk-count" aria-live="polite">
                            {t('vault.bulk.selected', { count: selKeys.length })}
                          </span>
                          {selKeys.length < shownRows.length && (
                            <button type="button" className="vault-bulk-all"
                                    onClick={() => setSelKeys(shownRows.map((r) => r.vault_file_id))}>
                              {t('vault.bulk.selectAll', { n: shownRows.length })}
                            </button>
                          )}
                          <div className="vault-bulk-actions">
                            {bulkButton('download', <DownloadIcon />, t('vault.download.file'), handleBulkDownload)}
                            {bulkButton('share', <SharePersonIcon />, t('vault.share.share'), () => setBulkDialog('share'))}
                            {bulkButton('copy', <CopyIcon />, t('vault.bulk.copyTo'), () => setBulkDialog('copy'))}
                            {bulkButton('move', <MoveIcon />, t('vault.move.moveTo'), () => setBulkDialog('move'))}
                            {bulkButton('delete', <TrashIcon />, t('vault.delete'), () => setBulkDialog('delete'), true)}
                          </div>
                        </div>
                      )}
                      {/* Find by name, in the folder you are standing in. */}
                      {tableRows.length > 0 && (selKeys.length === 0 || fileQuery.trim() !== '') && (
                        <div className="vault-files-search">
                          <Input dir="auto"
                            allowClear
                            size="small"
                            value={fileQuery}
                            placeholder={t('vault.findInFolder')}
                            onChange={(e) => setFileQuery(e.target.value)}
                          />
                          {fileQuery.trim() && (
                            <span className="vault-cell-muted">
                              {t('vault.matches', { n: shownRows.length })}
                            </span>
                          )}
                        </div>
                      )}
                      {shownRows.length === 0 ? (
                        /* The empty-state art is a right-click target too.
                           `.vault-body-blank` below already carried the
                           folder's menu, but in an EMPTY folder this art fills
                           the body and the blank strip sits underneath it — so
                           right-clicking the "this folder is empty" message,
                           which is the whole of what you can see, hit no
                           handler at all. An empty folder is exactly when
                           "New folder" and "Upload" are most wanted (field
                           report 2026-08-29). */
                        <Dropdown
                          trigger={['contextMenu']}
                          overlayClassName="vault-ctx-menu"
                          menu={{
                            items: folderMenuItems(detail, access),
                            onClick: onFolderMenuClick(detail),
                          }}
                        >
                          <div className="vault-empty">
                            <EmptyFolderArt />
                            <h3>{t('vault.noFilesTitle')}</h3>
                            <p>{access.can_edit ? t('vault.noFilesBodyEditor') : t('vault.noFilesBodyViewer')}</p>
                          </div>
                        </Dropdown>
                      ) : (
                        <Table
                          rowKey="vault_file_id"
                          dataSource={shownRows}
                          columns={columns}
                          pagination={shownRows.length > 25 ? { pageSize: 25 } : false}
                          size="middle"
                          // Click the row, the file opens. No hunting for the
                          // right small button first.
                          className="vault-file-table"
                          tableLayout="fixed"
                          rowSelection={{
                            selectedRowKeys: selKeys,
                            columnWidth: COL_W.select,
                            onChange: (keys) => setSelKeys(keys.map(String)),
                            onSelect: (row: TableRow) => { selAnchor.current = row.vault_file_id; },
                            getCheckboxProps: (row: TableRow) => ({
                              'aria-label': t('vault.bulk.selectRow', { name: rowName(row) }),
                            }) as Record<string, string>,
                          }}
                          onChange={(_page, _filters, sorter) => {
                            const s = Array.isArray(sorter) ? sorter[0] : sorter;
                            setSortState({ key: s?.columnKey as string | undefined, order: s?.order ?? null });
                          }}
                          onRow={(row: TableRow) => ({
                            className: `vault-row-openable${row.__folder ? ' vault-row-folder' : ''}`,
                            // Shift-click must not paint a text selection
                            // across the rows it is selecting.
                            onMouseDown: (e: React.MouseEvent) => { if (e.shiftKey) e.preventDefault(); },
                            onClick: (e: React.MouseEvent) => {
                              // The checkbox cell is the selection's own control.
                              if ((e.target as HTMLElement).closest('.ant-table-selection-column')) return;
                              if (e.metaKey || e.ctrlKey) { toggleRow(row.vault_file_id); return; }
                              if (e.shiftKey) { selectRange(row.vault_file_id); return; }
                              if (row.__folder) selectFolder(row.__folder.folder_id);
                              else openFile(row);
                            },
                            // The same actions the buttons carry, reachable
                            // without them — they are the first thing to go
                            // when the window is narrow, and then there is no
                            // way to open, edit or share anything at all.
                            onContextMenu: (e: React.MouseEvent) => {
                              e.preventDefault();
                              if (row.__folder) ensureMenuAccess(row.__folder.folder_id);
                              setRowMenu({ x: e.clientX, y: e.clientY, row });
                            },
                            // Rows drag, the way Drive's do: files and
                            // subfolders can be picked up, and a subfolder row
                            // is also somewhere to drop them.
                            ...(access?.can_edit ? dragProps(row.__folder
                              ? {
                                  kind: 'folder',
                                  folderId: row.__folder.folder_id,
                                  name: row.__folder.folder_name,
                                  parentId: row.__folder.parent_folder_id,
                                }
                              : {
                                  kind: 'file',
                                  folderId: detail!.folder_id,
                                  fileId: row.file_id,
                                  name: row.file_display_name || row.file_name || '',
                                }) : {}),
                            ...(row.__folder ? dropProps(row.__folder) : {}),
                          })}
                        />
                      )}
                      {/* Always somewhere to right-click. A folder with one
                          file in it is otherwise a table with no free space at
                          all, and then the only way to add anything is the New
                          button — which is not where a person looks. */}
                      <Dropdown
                        trigger={['contextMenu']}
                        overlayClassName="vault-ctx-menu"
                        menu={{
                          items: folderMenuItems(detail, access),
                          onClick: onFolderMenuClick(detail),
                        }}
                      >
                        <div className="vault-body-blank" />
                      </Dropdown>
                    </div>

                    {detail.rollup_summary && (
                      <VaultSummarySheet
                        folderName={detail.folder_name}
                        summary={detail.rollup_summary}
                        updatedAt={detail.rollup_summary_updated_at}
                        open={rollupOpen}
                        onToggle={setRollupOpen}
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            {shareNode && access && (
              <VaultShareDrawer node={shareNode} access={access} onClose={() => setShareNode(null)} />
            )}
            <VaultAccessMapDrawer
              open={accessMapOpen}
              onClose={() => setAccessMapOpen(false)}
              onOpenNode={revealNode}
            />
            {/* A right-click menu has nothing to hang off in a table, so it
                hangs off a point: a 1px anchor placed where the pointer was. */}
            {rowMenu && (
              <Dropdown
                open
                trigger={[]}
                overlayClassName="vault-ctx-menu"
                onOpenChange={(o) => { if (!o) setRowMenu(null); }}
                menu={rowMenu.row.__folder
                  ? {
                      items: folderMenuItems(
                        rowMenu.row.__folder,
                        menuAccess[rowMenu.row.__folder.folder_id] || access,
                      ),
                      onClick: (info) => {
                        onFolderMenuClick(rowMenu.row.__folder!)?.(info);
                        setRowMenu(null);
                      },
                    }
                  : { items: fileMenuItems(), onClick: (info) => {
                      onFileMenuClick(rowMenu.row)?.(info);
                      setRowMenu(null);
                    } }}
              >
                <div style={{ position: 'fixed', left: rowMenu.x, top: rowMenu.y, width: 1, height: 1 }} />
              </Dropdown>
            )}

            {viewFile && access && (
              <VaultFileViewer file={viewFile} access={access} onClose={() => setViewFile(null)} />
            )}
            {mapFile && (
              <VaultFileViewer
                file={mapFile.row}
                access={mapFile.access}
                onClose={() => setMapFile(null)}
              />
            )}

            {/* Deleting a folder is confirmed wherever it was asked for — the
                toolbar or a right-click — and states FACTS: what dies with it,
                what survives elsewhere, who loses reach. Never a bare
                "are you sure". */}
            <Modal
              open={!!confirmDelete}
              title={t('vault.deleteFolderConfirm')}
              okText={t('vault.delete')}
              cancelText={t('vault.cancel')}
              okButtonProps={{ danger: true }}
              onOk={() => { const f = confirmDelete; setConfirmDelete(null); if (f) handleDeleteFolder(f); }}
              onCancel={() => setConfirmDelete(null)}
              width={460}
            >
              <p className="vault-confirm-name">{confirmDelete?.folder_name}</p>
              {deleteImpact ? (
                <>
                  <ImpactRow head={t('vault.deleteTruth.contents', {
                    folders: deleteImpact.folders, files: deleteImpact.files_total,
                  })} />
                  <DeleteTruthRows impact={deleteImpact} t={t} />
                </>
              ) : confirmDelete && <Spin size="small" />}
            </Modal>

            {/* Removing a file states the one fact that matters most: does it
                die outright, or does it live on somewhere else — and where. */}
            <Modal
              open={!!confirmRemove}
              title={t('vault.removeFileConfirm')}
              okText={t('vault.remove')}
              cancelText={t('vault.cancel')}
              okButtonProps={{ danger: true }}
              onOk={() => {
                const f = confirmRemove;
                setConfirmRemove(null);
                if (f) handleRemoveFile(f.file_id);
              }}
              onCancel={() => setConfirmRemove(null)}
              width={460}
            >
              <p className="vault-confirm-name">
                {confirmRemove?.file_display_name || confirmRemove?.file_name}
              </p>
              {removeImpact ? (
                <>
                  {removeImpact.deleted_forever ? (
                    <ImpactRow tone="lose" head={t('vault.removeTruth.forever')} />
                  ) : (
                    <ImpactRow tone="keep" head={t('vault.removeTruth.keptIn', {
                      folders: removeImpact.kept_in
                        .map((k) => k.folder_name || '').filter(Boolean).join(', '),
                    })} />
                  )}
                  {removeImpact.agents.lose.length > 0 && (
                    <ImpactRow
                      tone="lose"
                      head={t('vault.deleteTruth.agentsLose', {
                        n: removeImpact.agents.lose.length,
                      })}
                      items={removeImpact.agents.lose.map((s) => subjectLabel(s, t))}
                    />
                  )}
                  {removeImpact.agents.keep.length > 0 && (
                    <ImpactRow
                      tone="keep"
                      head={t('vault.removeTruth.agentsKeep', {
                        n: removeImpact.agents.keep.length,
                      })}
                      items={removeImpact.agents.keep.map((s) => subjectLabel(s, t))}
                    />
                  )}
                </>
              ) : confirmRemove && <Spin size="small" />}
            </Modal>

            {/* "Move to…" / "Copy to…" — destination picker plus the
                consequences, confirmed before anything happens. */}
            {moveState && (
              <VaultMoveDialog
                source={moveState.source}
                mode={moveState.mode}
                presetDest={moveState.presetDest}
                presetImpact={moveState.presetImpact}
                onCancel={() => setMoveState(null)}
                onMoved={(destId) => {
                  const moved = moveState.source;
                  setMoveState(null);
                  if (moveState.mode === 'copy') {
                    // A copy changes nothing at the source; show the copy.
                    selectFolder(destId);
                  } else {
                    afterMove(moved, destId);
                  }
                }}
              />
            )}

            {/* The selection's own Move to… / Copy to… — the same picker,
                refusing every selected folder and what is beneath it. */}
            {(bulkDialog === 'move' || bulkDialog === 'copy') && detail && (
              <VaultMoveDialog
                source={{ kind: 'folder', name: '', folderId: detail.folder_id }}
                mode={bulkDialog}
                bulk={{
                  items: stableItems,
                  onDone: (outcome, dest) => {
                    const verb = bulkDialog;
                    setBulkDialog(null);
                    reportBulk(verb, outcome);
                    if (verb === 'move') keepUnfinished(outcome);
                    afterBulk(dest.folderId);
                  },
                }}
                onMoved={() => undefined}
                onCancel={() => setBulkDialog(null)}
              />
            )}

            {bulkDialog === 'share' && access && selectedItems.length > 0 && (
              <VaultShareDrawer
                node={selectedItems[0].kind === 'folder'
                  ? { folder_id: selectedItems[0].folder_id, name: rowName(selectedRows[0]) }
                  : { file_id: selectedItems[0].file_id, name: rowName(selectedRows[0]) }}
                access={access}
                onClose={() => setBulkDialog(null)}
                bulk={{
                  items: selectedItems,
                  names: selectedRows.map(rowName),
                  onDone: (outcome) => {
                    setBulkDialog(null);
                    reportBulk('share', outcome);
                    if (detail) loadDetail(detail.folder_id);
                  },
                }}
              />
            )}

            {/* ONE confirmation for the whole selection, naming the count and
                stating the facts — never N dialogs, never a bare "sure?". */}
            <Modal
              open={bulkDialog === 'delete'}
              title={t('vault.bulk.deleteTitle', { count: selectedItems.length })}
              okText={t('vault.delete')}
              cancelText={t('vault.cancel')}
              okButtonProps={{ danger: true, loading: bulkBusy }}
              onOk={runBulkDelete}
              onCancel={() => setBulkDialog(null)}
              width={480}
            >
              <ul className="vault-bulk-names">
                {selectedRows.slice(0, 8).map((row) => (
                  <li key={row.vault_file_id}><span dir="auto">{rowName(row)}</span></li>
                ))}
                {selectedRows.length > 8 && (
                  <li className="more">{t('vault.deleteTruth.andMore', { n: selectedRows.length - 8 })}</li>
                )}
              </ul>
              {selectedRows.some((r) => r.__folder) && (
                <ImpactRow head={t('vault.bulk.deleteFoldersNote')} />
              )}
              {bulkDeleteImpact ? (
                <>
                  <ImpactRow head={bulkDeleteImpact.folders
                    ? t('vault.bulk.deleteContents', {
                      folders: bulkDeleteImpact.folders, files: bulkDeleteImpact.files_total,
                    })
                    : t('vault.bulk.deleteContentsFiles', { files: bulkDeleteImpact.files_total })} />
                  <DeleteTruthRows impact={bulkDeleteImpact} t={t} />
                </>
              ) : bulkDialog === 'delete' && <Spin size="small" />}
            </Modal>
          </div>
        </main>
      </div>
    </div>
  );
}

export default VaultPage;
